// CST-to-AST converter: transforms Lezer parse tree into Pathogen AST nodes.
// The AST types are defined in ./ast.ts — this module produces identical output
// to the Parsimmon parser so the evaluator and language-services work unchanged.

import type { Tree, SyntaxNode, TreeCursor } from '@lezer/common';
import { matchFunctionNotation } from '../css-value-utils';
import type {
  Program,
  Statement,
  Expression,
  Comment,
  CornerOpAnnotation,
  LabelAnnotation,
  PathCommandAnnotations,
  LetDeclaration,
  AssignmentStatement,
  IndexedAssignmentStatement,
  MemberAssignmentStatement,
  ExpressionStatement,
  ForLoop,
  ForEachLoop,
  IfStatement,
  SwitchStatement,
  SwitchCase,
  SwitchDefault,
  SwitchExpression,
  SwitchArm,
  CasePattern,
  RangeExpression,
  RangePattern,
  FunctionDefinition,
  ReturnStatement,
  BreakStatement,
  ContinueStatement,
  EnumDefinition,
  PathCommand,
  PathArg,
  CalcExpression,
  FunctionCall,
  TernaryExpression,
  BinaryExpression,
  UnaryExpression,
  MemberExpression,
  IndexExpression,
  MethodCallExpression,
  NullLiteral,
  BooleanLiteral,
  SpreadElement,
  ArrayLiteral,
  ObjectLiteral,
  ObjectProperty,
  ArrayDestructuringPattern,
  ObjectDestructuringPattern,
  LayerDefinition,
  ViewBoxDefinition,
  LayerApplyBlock,
  LayerConstructorExpression,
  TextStatement,
  TspanStatement,
  TemplateLiteral,
  StyleBlockLiteral,
  StyleProperty,
  PathBlockExpression,
  TextBlockExpression,
  ColorLiteral,
  Identifier,
  NumberLiteral,
  StringLiteral,
  FontDirective,
  SourceLocation,
  TextBodyItem,
} from './ast';
import { statementForExpression } from './expression-statement';
import { pathArgCommentRanges } from './path-args-tokenizer';
import { RANGE_IN_PATH_ARGS } from './range-value-errors';

// --- Expression parser reference (set by index.ts to break circular dependency) ---
interface ExpressionParser {
  parse(input: string): { status: boolean; value: any };
  /** Offset into `input` of the first syntax error, for callers that got `status: false`. */
  errorOffset?(input: string): number | null;
}
let _expressionParser: ExpressionParser | null = null;

export function setExpressionParser(parser: ExpressionParser): void {
  _expressionParser = parser;
}

function parseExpressionString(exprStr: string): Expression | null {
  if (!_expressionParser) return null;
  try {
    const result = _expressionParser.parse(exprStr);
    if (result.status) return result.value;
  } catch { /* ignore */ }
  return null;
}

/**
 * Parse an expression string and adjust all SourceLocations to be relative
 * to a position in the original source. Used when we extract sub-expressions
 * (e.g., calc() inner content, if-condition, return value) and parse them
 * with Parsimmon — the resulting locations are relative to the sub-string
 * and need to be adjusted to the original source position.
 */
function parseExpressionAt(exprStr: string, sourceOffset: number, source: string): Expression | null {
  const expr = parseExpressionString(exprStr);
  if (!expr) return null;
  // Calculate the line/column offset from the source position
  const targetLoc = offsetToLoc(sourceOffset, source);
  adjustLocations(expr, targetLoc.line - 1, targetLoc.column - 1, sourceOffset);
  return expr;
}

// Wrapper offset: `let _ = ` is 8 characters
const WRAP_OFFSET = 8;

function adjustLocations(
  node: any,
  lineOffset: number,
  colOffset: number,
  sourceOffset: number,
  seen: Set<object> = new Set(),
): void {
  if (!node || typeof node !== 'object') return;
  // AST nodes may share a single loc object (e.g. MethodCallExpression and its
  // object Identifier) — adjust each loc exactly once or lines drift.
  if (node.loc && !seen.has(node.loc)) {
    seen.add(node.loc);
    // The wrapped text is `let _ = ` + the expression, and the expression
    // starts at sourceOffset in the document, so every node's offset moves
    // by the same amount whatever line it is on. Columns only shift on the
    // first line, where the wrapper sits.
    node.loc.offset = node.loc.offset - WRAP_OFFSET + sourceOffset;
    if (node.loc.line === 1) {
      node.loc.line += lineOffset;
      node.loc.column = node.loc.column - WRAP_OFFSET + colOffset;
    } else {
      node.loc.line += lineOffset;
    }
  }
  // Recurse into all object properties that could contain AST nodes
  for (const key of Object.keys(node)) {
    if (key === 'loc' || key === 'type') continue;
    const val = node[key];
    if (Array.isArray(val)) {
      for (const item of val) adjustLocations(item, lineOffset, colOffset, sourceOffset, seen);
    } else if (val && typeof val === 'object' && val.type) {
      adjustLocations(val, lineOffset, colOffset, sourceOffset, seen);
    }
  }
}

// --- Public API ---

// ─── Comments are trivia ────────────────────────────────────────────────────
//
// A `//` comment is a skipped token, so a LineComment node can turn up as a
// child of ANY node (between switch clauses, inside an array literal, in the
// middle of a method chain). This builder walks children with firstChild() /
// nextSibling() in ~150 places, and none of them should have to know that.
// So the cursor it walks with is COMMENT-BLIND: those two methods step over
// LineComment nodes. The few lists that keep their comments — statement
// lists, switch clause lists, text bodies — step with the *Raw variants at
// that one level; everything they call still walks blind.

type AstCursor = TreeCursor & { firstChildRaw: () => boolean; nextSiblingRaw: () => boolean };

function commentBlindCursor(tree: Tree): TreeCursor {
  const cursor = tree.cursor() as AstCursor;
  const rawFirstChild = cursor.firstChild.bind(cursor);
  const rawNextSibling = cursor.nextSibling.bind(cursor);
  cursor.firstChildRaw = rawFirstChild;
  cursor.nextSiblingRaw = rawNextSibling;
  cursor.nextSibling = (): boolean => {
    let steps = 0;
    while (rawNextSibling()) {
      steps++;
      if (cursor.name !== 'LineComment') return true;
    }
    // Only comments followed. Callers rely on a failed nextSibling() leaving
    // the cursor where it was (they go on to read it, or call parent()).
    for (; steps > 0; steps--) cursor.prevSibling();
    return false;
  };
  cursor.firstChild = (): boolean => {
    if (!rawFirstChild()) return false;
    if (cursor.name !== 'LineComment') return true;
    if (cursor.nextSibling()) return true;
    cursor.parent(); // nothing but comments in there
    return false;
  };
  return cursor;
}

/** Step INCLUDING comments — only for the lists that turn them into Comment nodes. */
function firstChildRaw(cursor: TreeCursor): boolean {
  return (cursor as AstCursor).firstChildRaw();
}
function nextSiblingRaw(cursor: TreeCursor): boolean {
  return (cursor as AstCursor).nextSiblingRaw();
}

/**
 * Convert a Lezer parse tree into a Pathogen AST Program node.
 * This produces the same AST as the Parsimmon parser's `parse()` function.
 */
export function buildAST(tree: Tree, source: string): Program {
  // Save/zero/restore loopDepth rather than plain-resetting: buildAST is
  // re-entered mid-build via parseExpressionString (if-conditions, for-each
  // iterables wrap sub-expressions as `let _ = expr;`), and a plain reset
  // would clobber the enclosing build's depth. The zero also guarantees a
  // previously thrown parse error can't leak stale depth into this build.
  const savedLoopDepth = loopDepth;
  loopDepth = 0;
  try {
  const body: Statement[] = [];
  const cursor = commentBlindCursor(tree);

  if (!firstChildRaw(cursor)) return { type: 'Program', body };

  do {
    // Statement-list comments stay in the AST (buildStatement builds them)
    // so the formatter can round-trip them.
    const stmt = buildStatement(cursor, source);
    if (stmt) body.push(stmt);
  } while (nextSiblingRaw(cursor));

  // Post-process: merge FontDirective + bare Number (Lezer splits them)
  for (let i = 0; i < body.length - 1; i++) {
    if (body[i].type === 'FontDirective' && !(body[i] as FontDirective).weight) {
      const next = body[i + 1];
      if (next.type === 'ExpressionStatement' &&
          (next as ExpressionStatement).expression.type === 'NumberLiteral') {
        (body[i] as FontDirective).weight =
          ((next as ExpressionStatement).expression as NumberLiteral).value;
        body.splice(i + 1, 1);
      }
    }
  }

  return { type: 'Program', body };
  } finally {
    loopDepth = savedLoopDepth;
  }
}

/**
 * Build a Program with comments interleaved in the body (parseWithComments).
 */
export function buildASTWithComments(tree: Tree, source: string): { program: Program; comments: Comment[] } {
  // Save/zero/restore — see buildAST for why (re-entrancy via parseExpressionString)
  const savedLoopDepth = loopDepth;
  loopDepth = 0;
  try {
    const body: Statement[] = [];
    const comments: Comment[] = [];
    const cursor = commentBlindCursor(tree);

    if (!firstChildRaw(cursor)) return { program: { type: 'Program', body }, comments };

    do {
      if (cursor.name === 'LineComment') {
        const comment = buildComment(cursor, source);
        comments.push(comment);
      } else {
        const stmt = buildStatement(cursor, source);
        if (stmt) body.push(stmt);
      }
    } while (nextSiblingRaw(cursor));

    return { program: { type: 'Program', body }, comments };
  } finally {
    loopDepth = savedLoopDepth;
  }
}

// --- Helpers ---

// ---- line / column for a source offset ----
//
// The definition (unchanged): `before = source.slice(0, offset)`; the line is the
// number of '\n'-separated pieces of `before`, the column is the length of the
// last piece plus one. '\r' is an ordinary column; columns are UTF-16 units.
//
// It used to be COMPUTED that way too — slice the prefix and split it, for every
// node and every path argument. That is O(source) per call: a 1.4 MB program
// took 18.8 s to parse, nearly all of it here. Line starts are now found once
// per source and binary-searched. tests/source-locations.test.ts holds the old
// definition verbatim as an oracle and checks every loc against it.

function computeLineStarts(source: string): number[] {
  const starts = [0];
  for (let at = source.indexOf('\n'); at !== -1; at = source.indexOf('\n', at + 1)) starts.push(at + 1);
  return starts;
}

// Sub-sources interleave with the document: every calc() argument and style
// value is parsed from its own short wrapped string (`let _ = …;`) and then the
// builder returns to the document. A one-entry cache is evicted by each of them
// and rebuilds the document's line starts per calc() — quadratic again, just
// somewhere else (measured: 11× time for 4× the calc() lines). So short sources
// never enter the cache, and the cache holds two entries for the rare case of
// one large source parsed inside another.
const SHORT_SOURCE = 512;
const lineStartsCache: { source: string; starts: number[] }[] = [];
let lineStartsCacheMisses = 0;

/**
 * How many times a LONG source's line starts were computed (cache misses).
 * @internal For tests/source-locations.test.ts, which pins the cache policy —
 * two entries, short sources bypass — deterministically instead of by timing.
 */
export function lineStartsCacheMissCount(): number {
  return lineStartsCacheMisses;
}

/** Offsets at which each line begins. */
function lineStartsFor(source: string): number[] {
  if (source.length <= SHORT_SOURCE) return computeLineStarts(source);
  for (let i = 0; i < lineStartsCache.length; i++) {
    if (lineStartsCache[i].source === source) {
      if (i !== 0) lineStartsCache.unshift(lineStartsCache.splice(i, 1)[0]);
      return lineStartsCache[0].starts;
    }
  }
  lineStartsCacheMisses++;
  lineStartsCache.unshift({ source, starts: computeLineStarts(source) });
  if (lineStartsCache.length > 2) lineStartsCache.pop();
  return lineStartsCache[0].starts;
}

/**
 * 1-based line and column of `offset`, clamped exactly as `slice(0, offset)` clamps.
 * @internal Exported for tests/source-locations.test.ts, which checks it against
 * the original slice-and-split definition.
 */
export function lineColumnAt(source: string, offset: number): { line: number; column: number } {
  let end = Number.isNaN(offset) ? 0 : Math.trunc(offset);
  end = end < 0 ? Math.max(source.length + end, 0) : Math.min(end, source.length);
  const starts = lineStartsFor(source);
  // Last line start at or before `end`: a newline at i starts a line at i + 1,
  // and it precedes `end` exactly when i + 1 <= end.
  let low = 0;
  let high = starts.length - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (starts[mid] <= end) low = mid;
    else high = mid - 1;
  }
  return { line: low + 1, column: end - starts[low] + 1 };
}

function loc(cursor: TreeCursor, source: string): SourceLocation {
  const { line, column } = lineColumnAt(source, cursor.from);
  return { line, column, offset: cursor.from };
}

function text(cursor: TreeCursor, source: string): string {
  return source.slice(cursor.from, cursor.to);
}

/** Get all named children of the current node. */
function namedChildren(cursor: TreeCursor, source: string): Array<{ name: string; from: number; to: number; text: string }> {
  const children: Array<{ name: string; from: number; to: number; text: string }> = [];
  if (cursor.firstChild()) {
    do {
      children.push({ name: cursor.name, from: cursor.from, to: cursor.to, text: source.slice(cursor.from, cursor.to) });
    } while (cursor.nextSibling());
    cursor.parent();
  }
  return children;
}

/** Get the node at a given path (e.g., "VariableName/Identifier"). */
function childText(cursor: TreeCursor, source: string, name: string): string | null {
  if (cursor.firstChild()) {
    do {
      if (cursor.name === name) {
        const t = text(cursor, source);
        cursor.parent();
        return t;
      }
      // Check nested: VariableName > Identifier
      if (cursor.firstChild()) {
        do {
          if (cursor.name === name) {
            const t = text(cursor, source);
            cursor.parent();
            cursor.parent();
            return t;
          }
        } while (cursor.nextSibling());
        cursor.parent();
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }
  return null;
}

// --- Loop-depth tracking for break/continue validation ---
//
// break/continue are valid only lexically inside a for-loop body (nested if
// branches included). fn bodies, lambdas/callback blocks, apply blocks, path
// blocks, and text-block top levels are boundaries: they reset the depth so
// a stray break/continue inside them errors at build time even when the
// surrounding source contains a loop. Depth is module state (the builder is
// synchronous and single-entry); buildAST/buildASTWithComments reset it so a
// previously thrown parse error cannot leave stale depth behind.
let loopDepth = 0;

function buildLoopBody(cursor: TreeCursor, source: string): Statement[] {
  loopDepth++;
  try {
    return buildBlock(cursor, source);
  } finally {
    loopDepth--;
  }
}

function atLoopBoundary<T>(build: () => T): T {
  const saved = loopDepth;
  loopDepth = 0;
  try {
    return build();
  } finally {
    loopDepth = saved;
  }
}

function buildLoopControl(
  cursor: TreeCursor,
  source: string,
  type: 'BreakStatement' | 'ContinueStatement',
): BreakStatement | ContinueStatement {
  const nodeLoc = loc(cursor, source);
  if (loopDepth === 0) {
    const keyword = type === 'BreakStatement' ? 'break' : 'continue';
    throw new Error(
      `Parse error at line ${nodeLoc.line}, column ${nodeLoc.column}: '${keyword}' is only valid inside a for loop`,
    );
  }
  return { type, loc: nodeLoc };
}

// --- Statement builders ---

function buildStatement(cursor: TreeCursor, source: string): Statement | null {
  switch (cursor.name) {
    case 'LineComment': return buildComment(cursor, source);
    case 'LetDeclaration': return buildLetDeclaration(cursor, source);
    case 'ExpressionStatement': return buildExpressionStatement(cursor, source);
    case 'ForLoop': return buildForLoop(cursor, source);
    case 'ForEachLoop': return buildForEachLoop(cursor, source);
    case 'IfStatement': return buildIfStatement(cursor, source);
    case 'SwitchStatement': return buildSwitchStatement(cursor, source);
    case 'FunctionDefinition': return buildFunctionDefinition(cursor, source);
    case 'ReturnStatement': return buildReturnStatement(cursor, source);
    case 'BreakStatement': return buildLoopControl(cursor, source, 'BreakStatement');
    case 'ContinueStatement': return buildLoopControl(cursor, source, 'ContinueStatement');
    case 'EnumDefinition': return buildEnumDefinition(cursor, source);
    case 'PathCommand': return buildPathCommand(cursor, source);
    case 'TspanStatement': return buildTspanStatement(cursor, source);
    case 'LayerDefinition': return buildLayerDefinition(cursor, source);
    case 'ViewBoxDefinition': return buildViewBoxDefinition(cursor, source);
    case 'LayerApplyBlock': return buildLayerApplyBlock(cursor, source);
    case 'TextStatement': return buildTextStatement(cursor, source);
    case 'FontDirective': return buildFontDirective(cursor, source);
    default: return null;
  }
}

function buildComment(cursor: TreeCursor, source: string): Comment {
  return {
    type: 'Comment',
    text: text(cursor, source),
    loc: loc(cursor, source),
  };
}

function buildLetDeclaration(cursor: TreeCursor, source: string): LetDeclaration {
  const nodeLoc = loc(cursor, source);
  const kind = cursor.node.firstChild?.name === 'const' ? 'const' : 'let';
  const children = namedChildren(cursor, source);

  let name = '';
  let pattern: ArrayDestructuringPattern | ObjectDestructuringPattern | undefined;
  let valueExpr: Expression | null = null;

  for (const child of children) {
    if (child.name === 'VariableName') {
      // Get the Identifier text inside VariableName
      name = child.text;
    } else if (child.name === 'ArrayDestructure') {
      pattern = buildArrayDestructure(cursor, source, child);
    } else if (child.name === 'ObjectDestructure') {
      pattern = buildObjectDestructure(cursor, source, child);
    }
  }

  // Build the value expression — it's the expression(s) after "="
  // May include postfix ops (ArgList, ".", "[") for function calls/member access
  cursor.firstChild();
  let foundEquals = false;
  do {
    if (cursor.name === '=') foundEquals = true;
    else if (foundEquals && cursor.name !== ';') {
      valueExpr = buildExpressionWithPostfix(cursor, source);
      break;
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return {
    type: 'LetDeclaration',
    kind,
    name,
    pattern,
    value: valueExpr || { type: 'NullLiteral' },
    loc: nodeLoc,
  };
}

function buildArrayDestructure(_cursor: TreeCursor, source: string, _child: { from: number; to: number }): ArrayDestructuringPattern {
  // Parse the text "[a, b, ...rest]" manually since we have the range
  const raw = source.slice(_child.from, _child.to);
  const inner = raw.slice(1, -1).trim(); // Remove [ ]
  const parts = inner.split(',').map((s) => s.trim());
  const elements: string[] = [];
  let rest: string | undefined;

  for (const part of parts) {
    if (part.startsWith('...')) {
      rest = part.slice(3).trim();
    } else if (part) {
      elements.push(part);
    }
  }

  return { type: 'ArrayDestructuringPattern', elements, rest };
}

function buildObjectDestructure(_cursor: TreeCursor, source: string, _child: { from: number; to: number }): ObjectDestructuringPattern {
  const raw = source.slice(_child.from, _child.to);
  const inner = raw.slice(1, -1).trim();
  const parts = inner.split(',').map((s) => s.trim());
  const properties: Array<{ key: string; alias?: string }> = [];
  let rest: string | undefined;

  for (const part of parts) {
    if (part.startsWith('...')) {
      rest = part.slice(3).trim();
    } else if (part.includes(':')) {
      const [key, alias] = part.split(':').map((s) => s.trim());
      properties.push({ key, alias });
    } else if (part) {
      properties.push({ key: part });
    }
  }

  return { type: 'ObjectDestructuringPattern', properties, rest };
}

function buildExpressionStatement(cursor: TreeCursor, source: string): Statement {
  const nodeLoc = loc(cursor, source);

  // Check if this is an assignment: expression "=" expression ";"
  const children = namedChildren(cursor, source);
  const eqIdx = children.findIndex((c) => c.name === '=');

  if (eqIdx >= 0) {
    // Assignments cannot carry with/as clauses — reject rather than silently drop.
    if (children.some((c) => c.name === 'WithClause' || c.name === 'AsClause')) {
      throw new Error(
        `Parse error at line ${nodeLoc.line}: with/as clauses are only allowed on path commands and path-emitting function calls`,
      );
    }
    // This is an assignment. Build the LHS and RHS.
    const lhsChildren = children.slice(0, eqIdx);
    const rhsChildren = children.slice(eqIdx + 1).filter((c) => c.name !== ';');

    // Build LHS expression (with postfix for member/index assignment)
    cursor.firstChild();
    const lhs = buildExpressionWithPostfix(cursor, source);
    cursor.parent();

    // Build RHS expression — find the expression after "="
    cursor.firstChild();
    let rhs: Expression = { type: 'NullLiteral' };
    let foundEq = false;
    do {
      if (cursor.name === '=') foundEq = true;
      else if (foundEq && cursor.name !== ';') {
        rhs = buildExpressionWithPostfix(cursor, source);
        break;
      }
    } while (cursor.nextSibling());
    cursor.parent();

    // Determine assignment type based on LHS structure
    if (lhs.type === 'MemberExpression') {
      return {
        type: 'MemberAssignmentStatement',
        object: lhs.object,
        property: lhs.property,
        value: rhs,
        loc: nodeLoc,
      } as MemberAssignmentStatement;
    }
    if (lhs.type === 'IndexExpression') {
      return {
        type: 'IndexedAssignmentStatement',
        object: lhs.object,
        index: lhs.index,
        value: rhs,
        loc: nodeLoc,
      } as IndexedAssignmentStatement;
    }
    if (lhs.type === 'Identifier') {
      return {
        type: 'AssignmentStatement',
        name: lhs.name,
        value: rhs,
        loc: nodeLoc,
      } as AssignmentStatement;
    }
  }

  // Not an assignment — build as expression statement with postfix chain
  cursor.firstChild();
  const expression = buildExpressionWithPostfix(cursor, source);
  cursor.parent();

  const annotations = collectAnnotations(cursor, source);

  // Propagate the statement's location to the expression for error reporting
  if (expression && typeof expression === 'object' && !('loc' in expression && (expression as any).loc?.line > 1)) {
    (expression as any).loc = nodeLoc;
  }

  // A standalone call is a PathCommand with an empty command (the evaluator
  // accumulates its path data there), e.g. circle(50, 50, 25) →
  // PathCommand("", [FunctionCall("circle", ...)]); anything else is an
  // ExpressionStatement. statementForExpression is the one place that rule
  // lives — the evaluator reuses it for builder blocks.
  const isCall = expression.type === 'FunctionCall' || expression.type === 'MethodCallExpression';
  if (annotations && !isCall) {
    throw new Error(
      `Parse error at line ${nodeLoc.line}: with/as clauses are only allowed on path commands and path-emitting function calls`,
    );
  }

  return statementForExpression(expression, nodeLoc, annotations);
}

function buildForLoop(cursor: TreeCursor, source: string): ForLoop {
  const nodeLoc = loc(cursor, source);
  let variable = '';
  let start: Expression = { type: 'NumberLiteral', value: 0 };
  let end: Expression = { type: 'NumberLiteral', value: 0 };
  let body: Statement[] = [];
  let inclusive = true;

  cursor.firstChild();
  let phase = 0; // 0=before range, 1=after start, 2=after rangeOp
  const noteRangeOp = (name: string): boolean => {
    if (name !== 'RangeOp' && name !== 'HalfOpenRangeOp') return false;
    inclusive = name === 'RangeOp';
    phase = 2;
    return true;
  };
  do {
    if (cursor.name === 'VariableName') {
      variable = text(cursor, source);
    } else if (noteRangeOp(cursor.name)) {
      // range operator handled
    } else if (phase === 0 && isExpressionNode(cursor.name)) {
      // Postfix chains (arr.length, bounds[0], top()) sit at sibling
      // level; the postfix-aware builder consumes them and leaves the
      // cursor on the first UNCONSUMED token — which for a start bound
      // is the range operator itself. Check before the loop advances past
      // it, or phase 2 never arrives and the end bound silently defaults.
      start = buildExpressionWithPostfix(cursor, source);
      if (!noteRangeOp(cursor.name as string)) phase = 1;
    } else if (phase === 2 && isExpressionNode(cursor.name)) {
      end = buildExpressionWithPostfix(cursor, source);
    } else if (cursor.name === 'Block') {
      body = buildLoopBody(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'ForLoop', variable, start, end, inclusive, body, loc: nodeLoc };
}

function buildForEachLoop(cursor: TreeCursor, source: string): ForEachLoop {
  const nodeLoc = loc(cursor, source);
  let variable = '';
  let indexVariable: string | undefined;
  let iterable: Expression = { type: 'NullLiteral' };
  let body: Statement[] = [];

  cursor.firstChild();
  let foundIn = false;
  do {
    if (cursor.name === 'VariableName' && !foundIn) {
      variable = text(cursor, source);
    } else if (cursor.name === 'ForEachDestructure') {
      // [item, index]
      const vars = extractVariableNames(cursor, source);
      if (vars.length >= 1) variable = vars[0];
      if (vars.length >= 2) indexVariable = vars[1];
    } else if (cursor.name === 'in') {
      foundIn = true;
    } else if (foundIn && isExpressionNode(cursor.name)) {
      // The iterable may have postfix ops (.slice(), etc.) that span multiple siblings.
      // Extract from current position to ')' and parse with Parsimmon.
      const iterStart = cursor.from;
      let iterEnd = cursor.to;
      while (cursor.nextSibling()) {
        if (cursor.name === ')') break;
        iterEnd = cursor.to;
      }
      // parseExpressionAt, not parseExpressionString: the header is parsed from
      // its own `let _ = …;` wrapper, and without rebasing every error in a
      // for-each header reported "Line 1, col 9" wherever the loop was.
      const iterRaw = source.slice(iterStart, iterEnd);
      const iterLead = iterRaw.length - iterRaw.trimStart().length;
      const parsed = parseExpressionAt(iterRaw.trim(), iterStart + iterLead, source);
      if (parsed) iterable = parsed;
    } else if (cursor.name === 'Block') {
      body = buildLoopBody(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'ForEachLoop', variable, indexVariable, iterable, body, loc: nodeLoc };
}

function buildIfStatement(cursor: TreeCursor, source: string): IfStatement {
  const nodeLoc = loc(cursor, source);
  let condition: Expression = { type: 'BooleanLiteral', value: true };
  let consequent: Statement[] = [];
  let alternate: Statement[] | null = null;

  cursor.firstChild();
  do {
    if (cursor.name === '(') {
      // Build the condition from all tokens between ( and )
      // Use the source text range and parse it with Parsimmon for full fidelity
      const condStart = cursor.to; // After '('
      // Find the matching ')'
      let depth = 1;
      let condEnd = condStart;
      while (cursor.nextSibling()) {
        if (cursor.name === '(') depth++;
        else if (cursor.name === ')') {
          depth--;
          if (depth === 0) { condEnd = cursor.from; break; }
        }
      }
      const condStr = source.slice(condStart, condEnd).trim();
      if (condStr) {
        const condOffset = condStart + (source.slice(condStart, condEnd).length - source.slice(condStart, condEnd).trimStart().length);
        const parsed = parseExpressionAt(condStr, condOffset, source);
        if (parsed) condition = parsed;
      }
    } else if (cursor.name === 'Block' && consequent.length === 0 && !alternate) {
      consequent = buildBlock(cursor, source);
    } else if (cursor.name === 'ElseClause') {
      alternate = buildElseClause(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'IfStatement', condition, consequent, alternate, loc: nodeLoc };
}

function buildElseClause(cursor: TreeCursor, source: string): Statement[] {
  cursor.firstChild();
  let result: Statement[] = [];
  do {
    if (cursor.name === 'Block') {
      result = buildBlock(cursor, source);
    } else if (cursor.name === 'IfStatement') {
      result = [buildIfStatement(cursor, source)];
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return result;
}

// --- Switch statements ---

function parseErrorAt(l: SourceLocation | undefined, message: string): Error {
  return new Error(l ? `Parse error at line ${l.line}, column ${l.column}: ${message}` : `Parse error: ${message}`);
}

type CaseBodyBuilder = (cursor: TreeCursor) => Statement[];

function buildSwitchStatement(cursor: TreeCursor, source: string): SwitchStatement {
  // Case bodies go through plain buildBlock (never buildLoopBody): loopDepth
  // is untouched, so break/continue inside a case target the enclosing loop
  // and are still rejected when there is none.
  return buildSwitchLike(cursor, source, 'CaseClause', 'DefaultClause', (c) => buildBlock(c, source));
}

function buildTextSwitchStatement(cursor: TreeCursor, source: string): SwitchStatement {
  return buildSwitchLike(
    cursor, source, 'TextCaseClause', 'TextDefaultClause',
    (c) => buildInlineTextBody(c, source) as unknown as Statement[],
  );
}

function buildSwitchLike(
  cursor: TreeCursor,
  source: string,
  caseName: string,
  defaultName: string,
  buildBody: CaseBodyBuilder,
): SwitchStatement {
  const nodeLoc = loc(cursor, source);
  let discriminant: Expression | null = null;
  const cases: SwitchCase[] = [];
  let defaultCase: SwitchDefault | null = null;
  // Comments between clauses — most often a clause that has been commented
  // out — lead the clause that follows them; the ones after the last clause
  // trail the switch.
  let pendingComments: Comment[] = [];

  firstChildRaw(cursor);
  let inParens = false;
  do {
    if (cursor.name === 'LineComment') {
      // Inside the (discriminant) a comment has no clause to lead: it stays
      // uncaptured, and the formatter keeps the statement verbatim.
      if (!inParens && discriminant) pendingComments.push(buildComment(cursor, source));
    } else if (cursor.name === '(') {
      inParens = true;
    } else if (cursor.name === ')') {
      inParens = false;
    } else if (inParens && !discriminant && isExpressionNode(cursor.name)) {
      // The postfix-aware builder leaves the cursor on the first unconsumed
      // sibling, which for a discriminant is the closing paren.
      discriminant = buildExpressionWithPostfix(cursor, source);
      if ((cursor.name as string) === ')') inParens = false;
    } else if (cursor.name === caseName) {
      if (defaultCase) {
        throw parseErrorAt(loc(cursor, source), "'default' must be the last clause in a switch");
      }
      const clause = buildCaseClause(cursor, source, buildBody);
      if (pendingComments.length > 0) clause.leadingComments = pendingComments;
      pendingComments = [];
      cases.push(clause);
    } else if (cursor.name === defaultName) {
      if (defaultCase) {
        throw parseErrorAt(loc(cursor, source), "A switch may have only one 'default' clause");
      }
      defaultCase = buildDefaultClause(cursor, source, buildBody);
      if (pendingComments.length > 0) defaultCase.leadingComments = pendingComments;
      pendingComments = [];
    }
  } while (nextSiblingRaw(cursor));
  cursor.parent();

  return {
    type: 'SwitchStatement',
    discriminant: discriminant ?? { type: 'NullLiteral' },
    cases,
    defaultCase,
    ...(pendingComments.length > 0 ? { trailingComments: pendingComments } : {}),
    loc: nodeLoc,
  };
}

function buildCaseClause(cursor: TreeCursor, source: string, buildBody: CaseBodyBuilder): SwitchCase {
  const nodeLoc = loc(cursor, source);
  const patterns: CasePattern[] = [];
  let guard: Expression | null = null;
  let body: Statement[] = [];

  cursor.firstChild();
  do {
    if (cursor.name === 'CasePattern') {
      patterns.push(buildCasePattern(cursor, source));
    } else if (cursor.name === 'WhereGuard') {
      guard = buildWhereGuard(cursor, source);
    } else if (cursor.name === 'Block' || cursor.name === '{') {
      // Block: the statement form. '{': the text form's inline body, which
      // buildInlineTextBody consumes up to the matching '}'.
      body = buildBody(cursor);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  checkAlternativeBindings(patterns);
  return { type: 'SwitchCase', patterns, guard, body, loc: nodeLoc };
}

function buildDefaultClause(cursor: TreeCursor, source: string, buildBody: CaseBodyBuilder): SwitchDefault {
  const nodeLoc = loc(cursor, source);
  let body: Statement[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === 'Block' || cursor.name === '{') body = buildBody(cursor);
  } while (cursor.nextSibling());
  cursor.parent();
  return { type: 'SwitchDefault', body, loc: nodeLoc };
}

function buildWhereGuard(cursor: TreeCursor, source: string): Expression {
  let guard: Expression = { type: 'BooleanLiteral', value: true };
  cursor.firstChild();
  do {
    if (cursor.name !== 'where' && isExpressionNode(cursor.name)) {
      guard = buildExpressionWithPostfix(cursor, source);
      break;
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return guard;
}

function buildCasePattern(cursor: TreeCursor, source: string): CasePattern {
  const patLoc = loc(cursor, source);
  let pattern: CasePattern;
  cursor.firstChild();
  if (cursor.name === 'RangePattern') {
    pattern = buildRangePattern(cursor, source, patLoc);
  } else {
    pattern = asCasePattern(buildExpressionWithPostfix(cursor, source), patLoc);
  }
  cursor.parent();
  return pattern;
}

// The bound walk shared by RangePattern (`case a..b`, either bound optional)
// and RangeExpression (`(a..b)`, both required). Children are the optional
// parentheses, the bounds with their postfix chains flattened to sibling
// level, and the operator token.
function readRangeBounds(
  cursor: TreeCursor,
  source: string,
): { start: Expression | null; end: Expression | null; inclusive: boolean } {
  let start: Expression | null = null;
  let end: Expression | null = null;
  let inclusive = true;
  let phase = 0; // 0 = before the operator, 2 = after it

  const noteOp = (name: string): boolean => {
    if (name !== 'RangeOp' && name !== 'HalfOpenRangeOp') return false;
    inclusive = name === 'RangeOp';
    phase = 2;
    return true;
  };

  cursor.firstChild();
  do {
    if (noteOp(cursor.name)) {
      // operator token handled
    } else if (phase === 0 && isExpressionNode(cursor.name)) {
      // Same cursor-rest hazard as buildForLoop: the postfix-aware builder
      // may stop ON the range operator, so check before the loop advances.
      start = buildExpressionWithPostfix(cursor, source);
      noteOp(cursor.name as string);
    } else if (phase === 2 && isExpressionNode(cursor.name)) {
      end = buildExpressionWithPostfix(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { start, end, inclusive };
}

function buildRangePattern(cursor: TreeCursor, source: string, patLoc: SourceLocation): RangePattern {
  return { type: 'RangePattern', ...readRangeBounds(cursor, source), loc: patLoc };
}

// `(a..b)` — the grammar requires both bounds, so a missing one means the tree
// came from error recovery. parse() rejects error trees before building (with
// the describeBareRange message); only the LENIENT path (parseLezer, used by
// the language services mid-typing) reaches the fallback, and it must not
// throw — a half-typed `(1..` would otherwise take the whole scope tree down.
function buildRangeExpression(cursor: TreeCursor, source: string): RangeExpression {
  const nodeLoc = loc(cursor, source);
  const { start, end, inclusive } = readRangeBounds(cursor, source);
  return {
    type: 'RangeExpression',
    start: start ?? { type: 'NullLiteral' },
    end: end ?? { type: 'NullLiteral' },
    inclusive,
    loc: nodeLoc,
  };
}

// Cover-grammar reinterpretation: the grammar parses `case [a, b]` and
// `case {x, y}` as array/object LITERALS (a dedicated destructure alternative
// would conflict with them). Literal-shaped patterns that bind names become
// destructuring patterns; anything else is a value pattern. A literal that
// cannot bind (`case [1, 2]`) is rejected — arrays and objects are never
// `==`-comparable, so a value reading could never match anyway.
function asCasePattern(expr: Expression, patLoc: SourceLocation): CasePattern {
  // `case (1..5)` is the pattern `case 1..5`, never a comparison against the
  // array the parenthesized range means in value position (arrays are not
  // `==`-comparable, so that reading could never match). Only a BARE range
  // converts: `case (1..5).length` reaches here as a MemberExpression.
  if (expr.type === 'RangeExpression') {
    return { type: 'RangePattern', start: expr.start, end: expr.end, inclusive: expr.inclusive, loc: patLoc };
  }
  if (expr.type === 'ArrayLiteral') {
    const elements: string[] = [];
    let rest: string | undefined;
    expr.elements.forEach((el, i) => {
      if (el.type === 'Identifier') {
        elements.push(el.name);
      } else if (el.type === 'SpreadElement' && el.argument.type === 'Identifier' && i === expr.elements.length - 1) {
        rest = el.argument.name;
      } else {
        throw parseErrorAt(patLoc, 'Array patterns in a case bind names only — write case [first, second] or case [head, ...rest]');
      }
    });
    return { type: 'ArrayDestructuringPattern', elements, rest, loc: patLoc };
  }
  if (expr.type === 'ObjectLiteral') {
    const properties: { key: string; alias?: string }[] = [];
    let rest: string | undefined;
    expr.properties.forEach((prop, i) => {
      if (prop.type === 'SpreadElement') {
        if (prop.argument.type === 'Identifier' && i === expr.properties.length - 1) {
          rest = prop.argument.name;
        } else {
          throw parseErrorAt(patLoc, 'Object patterns in a case may end with a single ...rest name');
        }
      } else if (prop.shorthand) {
        properties.push({ key: prop.key });
      } else if (prop.value.type === 'Identifier') {
        properties.push({ key: prop.key, alias: prop.value.name });
      } else {
        throw parseErrorAt(patLoc, 'Object patterns in a case bind names only — write case {x, y} or case {x: px}');
      }
    });
    return { type: 'ObjectDestructuringPattern', properties, rest, loc: patLoc };
  }
  return { type: 'ValuePattern', value: expr, loc: patLoc };
}

// --- Switch expressions ---

function buildSwitchExpression(cursor: TreeCursor, source: string): SwitchExpression {
  const nodeLoc = loc(cursor, source);
  let discriminant: Expression | null = null;
  const arms: SwitchArm[] = [];
  let defaultValue: Expression | null = null;

  cursor.firstChild();
  let inParens = false;
  do {
    if (cursor.name === '(') {
      inParens = true;
    } else if (cursor.name === ')') {
      inParens = false;
    } else if (inParens && !discriminant && isExpressionNode(cursor.name)) {
      discriminant = buildExpressionWithPostfix(cursor, source);
      if ((cursor.name as string) === ')') inParens = false;
    } else if (cursor.name === 'CaseArm') {
      if (defaultValue) {
        throw parseErrorAt(loc(cursor, source), "'default' must be the last arm in a switch expression");
      }
      arms.push(buildSwitchArm(cursor, source));
    } else if (cursor.name === 'DefaultArm') {
      if (defaultValue) {
        throw parseErrorAt(loc(cursor, source), "A switch expression may have only one 'default' arm");
      }
      defaultValue = buildDefaultArm(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  // The grammar makes the default arm mandatory; this is reachable only
  // through error recovery, where it still gives the language service a
  // precise message.
  if (!defaultValue) {
    throw parseErrorAt(nodeLoc, "A switch expression needs a 'default' arm so it always produces a value");
  }
  return {
    type: 'SwitchExpression',
    discriminant: discriminant ?? { type: 'NullLiteral' },
    arms,
    defaultValue,
    loc: nodeLoc,
  };
}

function buildSwitchArm(cursor: TreeCursor, source: string): SwitchArm {
  const nodeLoc = loc(cursor, source);
  const patterns: CasePattern[] = [];
  let guard: Expression | null = null;
  let value: Expression | null = null;

  cursor.firstChild();
  do {
    if (cursor.name === 'CasePattern') {
      patterns.push(buildCasePattern(cursor, source));
    } else if (cursor.name === 'WhereGuard') {
      guard = buildWhereGuard(cursor, source);
    } else if (cursor.name === '{') {
      value = readArmValue(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  checkAlternativeBindings(patterns);
  return { type: 'SwitchArm', patterns, guard, value: value ?? { type: 'NullLiteral' }, loc: nodeLoc };
}

function buildDefaultArm(cursor: TreeCursor, source: string): Expression {
  let value: Expression | null = null;
  cursor.firstChild();
  do {
    if (cursor.name === '{') value = readArmValue(cursor, source);
  } while (cursor.nextSibling());
  cursor.parent();
  return value ?? { type: 'NullLiteral' };
}

// Cursor on the arm's '{'; the arm holds exactly one expression (with its
// postfix chain) before the matching '}'. Leaves the cursor on or after '}'.
function readArmValue(cursor: TreeCursor, source: string): Expression | null {
  let value: Expression | null = null;
  while (cursor.nextSibling() && cursor.name !== '}') {
    if (!value && isExpressionNode(cursor.name)) {
      value = buildExpressionWithPostfix(cursor, source);
      if ((cursor.name as string) === '}') break;
    }
  }
  return value;
}

function patternBindingNames(p: CasePattern): string[] {
  if (p.type === 'ArrayDestructuringPattern') {
    return [...p.elements, ...(p.rest ? [p.rest] : [])].sort();
  }
  if (p.type === 'ObjectDestructuringPattern') {
    return [...p.properties.map((q) => q.alias ?? q.key), ...(p.rest ? [p.rest] : [])].sort();
  }
  return [];
}

// Swift's rule: comma alternatives share one body and one guard, so every
// alternative must bind exactly the same names.
function checkAlternativeBindings(patterns: CasePattern[]): void {
  if (patterns.length < 2) return;
  const describe = (names: string[]): string => (names.length ? names.join(', ') : 'nothing');
  const first = patternBindingNames(patterns[0]);
  for (let i = 1; i < patterns.length; i++) {
    const names = patternBindingNames(patterns[i]);
    if (names.join(',') !== first.join(',')) {
      throw parseErrorAt(
        patterns[i].loc ?? patterns[0].loc,
        `Every pattern in a case must bind the same names (this one binds ${describe(names)}, the first binds ${describe(first)})`,
      );
    }
  }
}

function buildFunctionDefinition(cursor: TreeCursor, source: string): FunctionDefinition {
  const nodeLoc = loc(cursor, source);
  let name = '';
  const params: string[] = [];
  let body: Statement[] = [];

  cursor.firstChild();
  let foundParen = false;
  let closedParen = false;
  do {
    if (cursor.name === 'VariableName' && !foundParen) {
      name = text(cursor, source);
    } else if (cursor.name === '(') {
      foundParen = true;
    } else if (cursor.name === ')') {
      closedParen = true;
    } else if (cursor.name === 'VariableName' && foundParen && !closedParen) {
      params.push(text(cursor, source));
    } else if (cursor.name === 'Block') {
      body = atLoopBoundary(() => buildBlock(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'FunctionDefinition', name, params, body, loc: nodeLoc };
}

function buildReturnStatement(cursor: TreeCursor, source: string): ReturnStatement {
  const nodeLoc = loc(cursor, source);
  // Extract text between 'return' and ';', parse with Parsimmon for full fidelity
  const stmtStart = cursor.from;
  const stmtEnd = cursor.to;
  const stmtText = source.slice(stmtStart, stmtEnd);
  // Remove 'return' prefix and trailing ';'
  const exprText = stmtText.replace(/^\s*return\s+/, '').replace(/;\s*$/, '').trim();

  let value: Expression = { type: 'NullLiteral' };
  if (exprText) {
    // Calculate the offset where the expression starts in the source
    const returnMatch = stmtText.match(/^\s*return\s+/);
    const exprOffset = stmtStart + (returnMatch ? returnMatch[0].length : 7);
    const parsed = parseExpressionAt(exprText, exprOffset, source);
    if (parsed) value = parsed;
  }

  return { type: 'ReturnStatement', value, loc: nodeLoc };
}

function buildEnumDefinition(cursor: TreeCursor, source: string): EnumDefinition {
  const nodeLoc = loc(cursor, source);
  let name = '';
  const members: Array<{ name: string; value?: Expression }> = [];

  cursor.firstChild();
  do {
    if (cursor.name === 'VariableName' && !name) {
      name = text(cursor, source);
    } else if (cursor.name === 'EnumMember') {
      cursor.firstChild();
      let memberName = '';
      let memberValue: Expression | undefined;
      do {
        if (cursor.name === 'VariableName') memberName = text(cursor, source);
        else if (cursor.name !== '=' && isExpressionNode(cursor.name)) {
          memberValue = buildExpressionWithPostfix(cursor, source);
        }
      } while (cursor.nextSibling());
      cursor.parent();
      members.push({ name: memberName, value: memberValue });
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'EnumDefinition', name, members, loc: nodeLoc };
}

const CORNER_OP_KINDS = new Set(['fillet', 'chamfer', 'ellipticalFillet']);
const LABEL_KINDS = new Set(['segment', 'endpoint']);

/** Build a `with <cornerOp>(...)` clause. Cursor must be on WithClause. */
function buildWithClause(cursor: TreeCursor, source: string): CornerOpAnnotation {
  const clauseLoc = loc(cursor, source);
  let kind = '';
  let args: Expression[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === 'CornerOpCall') {
      cursor.firstChild();
      do {
        if (cursor.name === 'Identifier') kind = text(cursor, source);
        else if (cursor.name === 'ArgList') args = buildArgList(cursor, source);
      } while (cursor.nextSibling());
      cursor.parent();
    }
  } while (cursor.nextSibling());
  cursor.parent();
  if (!CORNER_OP_KINDS.has(kind)) {
    throw new Error(
      `Parse error at line ${clauseLoc.line}: unknown corner operation '${kind}' in with clause — expected fillet, chamfer, or ellipticalFillet`,
    );
  }
  return { kind: kind as CornerOpAnnotation['kind'], args, loc: clauseLoc };
}

/** Build an `as segment('x'), endpoint('y')` clause. Cursor must be on AsClause. */
function buildAsClause(cursor: TreeCursor, source: string): LabelAnnotation[] {
  const labels: LabelAnnotation[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === 'LabelCall') {
      const labelLoc = loc(cursor, source);
      let kind = '';
      let args: Expression[] = [];
      cursor.firstChild();
      do {
        if (cursor.name === 'Identifier') kind = text(cursor, source);
        else if (cursor.name === 'ArgList') args = buildArgList(cursor, source);
      } while (cursor.nextSibling());
      cursor.parent();
      if (!LABEL_KINDS.has(kind)) {
        throw new Error(
          `Parse error at line ${labelLoc.line}: unknown label kind '${kind}' in as clause — expected segment or endpoint`,
        );
      }
      if (args.length !== 1) {
        throw new Error(
          `Parse error at line ${labelLoc.line}: ${kind}() label expects exactly 1 argument (the name), got ${args.length}`,
        );
      }
      labels.push({ kind: kind as LabelAnnotation['kind'], name: args[0], loc: labelLoc });
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return labels;
}

/**
 * Collect WithClause/AsClause children of the node the cursor points into.
 * Cursor must be positioned ON the parent; iterates children non-destructively.
 */
function collectAnnotations(cursor: TreeCursor, source: string): PathCommandAnnotations | undefined {
  let cornerOp: CornerOpAnnotation | undefined;
  let labels: LabelAnnotation[] | undefined;
  cursor.firstChild();
  do {
    if (cursor.name === 'WithClause') cornerOp = buildWithClause(cursor, source);
    else if (cursor.name === 'AsClause') labels = buildAsClause(cursor, source);
  } while (cursor.nextSibling());
  cursor.parent();
  if (!cornerOp && !labels) return undefined;
  return { ...(cornerOp ? { cornerOp } : {}), ...(labels ? { labels } : {}) };
}

function buildPathCommand(cursor: TreeCursor, source: string): Statement {
  const nodeLoc = loc(cursor, source);
  let command = '';
  let argsText = '';
  let argsFrom = 0;

  cursor.firstChild();
  do {
    if (cursor.name === 'PathCommandLetter') {
      command = text(cursor, source);
    } else if (cursor.name === 'PathArgs') {
      // A comment inside an open paren is part of the token's text. Blank it
      // (same length, so every offset still points into the source) before
      // anything below reads the arguments.
      argsText = blankPathArgComments(text(cursor, source));
      argsFrom = cursor.from;
    }
  } while (cursor.nextSibling());
  cursor.parent();

  const annotations = collectAnnotations(cursor, source);

  // Fixup: if a single lowercase letter is followed by "." (member access),
  // this is actually variable.method(), not a path command.
  // e.g., "m.draw()" should be PathCommand("", [MethodCallExpr]) not PathCommand("m", [draw arg]).
  if (command.length === 1 && argsText.trimStart().startsWith('.')) {
    const fullExpr = command + argsText;
    // Use parseExpressionAt to get correct source locations
    const cmdOffset = nodeLoc.offset;
    const parsed = parseExpressionAt(fullExpr, cmdOffset, source);
    if (parsed) {
      // Wrap as PathCommand with empty command — the evaluator needs this
      // format to accumulate path data from method calls like draw().
      return { type: 'PathCommand', command: '', args: [parsed as PathArg], ...(annotations ? { annotations } : {}), loc: nodeLoc } as PathCommand;
    }
  }

  const args: PathArg[] = [];
  const trimmedArgs = argsText.trimStart();
  const trimOffset = argsText.length - trimmedArgs.length; // Leading whitespace skipped
  if (trimmedArgs.trim()) {
    assertNoRangeInPathArgs(trimmedArgs.trim(), argsFrom + trimOffset, source);
    args.push(...parsePathArgs(trimmedArgs.trim(), argsFrom + trimOffset, source));
  }
  return { type: 'PathCommand', command, args, ...(annotations ? { annotations } : {}), loc: nodeLoc } as PathCommand;
}

/**
 * Parse the raw PathArgs text into individual PathArg AST nodes.
 * This handles: numbers, identifiers, calc(), booleans, function calls,
 * member access, and index access.
 */
/**
 * A range is never a path argument, and the path-args shadow grammar is blind
 * to `..`: `L (1..3) 5` used to read as `1`, `.3`, `5`, and `L f((1..3)) 5`
 * passed `1` to f — wrong path data with no error. Reject it up front.
 *
 * Only spans the SHADOW grammar reads are scanned. `calc(…)` interiors and
 * array literals go to the real expression parser, where `(a..b)` is legal,
 * so those groups are skipped whole; call arguments and `[index]` suffixes
 * recurse through parsePathArgs and are scanned. Quoted strings are skipped.
 * A statement that starts with `(` on the line after a path command with no
 * `;` is swallowed into that command's args, which is why the message
 * mentions the semicolon.
 */
function blankPathArgComments(argsText: string): string {
  let out = argsText;
  for (const [from, to] of pathArgCommentRanges(argsText)) {
    out = out.slice(0, from) + ' '.repeat(to - from) + out.slice(to);
  }
  return out;
}

function assertNoRangeInPathArgs(argsText: string, baseOffset: number, source: string): void {
  if (!argsText.includes('..')) return;
  let pos = 0;
  while (pos < argsText.length) {
    const afterString = skipQuoted(argsText, pos);
    if (afterString !== pos) {
      pos = afterString;
      continue;
    }
    const ch = argsText[pos];
    if (ch === '(' && precededByCalc(argsText, pos)) {
      const inner = extractParenContent(argsText, pos);
      if (inner !== null) {
        pos += inner.length + 2;
        continue;
      }
    }
    // `[` directly after a name, `)` or `]` is an index suffix (shadow
    // grammar); anywhere else it opens an array literal (real parser).
    if (ch === '[' && !(pos > 0 && /[\w)\]]/.test(argsText[pos - 1]))) {
      const inner = extractBracketContent(argsText, pos);
      if (inner !== null) {
        pos += inner.length + 2;
        continue;
      }
    }
    if (ch === '.' && argsText[pos + 1] === '.') {
      if (argsText[pos + 2] === '.') {
        pos += 3; // spread
        continue;
      }
      const at = offsetToLoc(baseOffset + pos, source);
      throw parseErrorAt(at, RANGE_IN_PATH_ARGS);
    }
    pos++;
  }
}

/** Is the `(` at `openPos` the opener of a `calc(`? */
function precededByCalc(argsText: string, openPos: number): boolean {
  let end = openPos;
  while (end > 0 && /\s/.test(argsText[end - 1])) end--;
  if (end < 4 || argsText.slice(end - 4, end) !== 'calc') return false;
  return end === 4 || !/[\w.]/.test(argsText[end - 5]);
}

function parsePathArgs(argsText: string, baseOffset: number, source: string): PathArg[] {
  // Simple tokenizer for path args
  const args: PathArg[] = [];
  let pos = 0;

  while (pos < argsText.length) {
    // Skip whitespace
    while (pos < argsText.length && /\s/.test(argsText[pos])) pos++;
    if (pos >= argsText.length) break;

    const ch = argsText[pos];

    // Number: digits, dots, minus
    if (/[\d.]/.test(ch) || (ch === '-' && pos + 1 < argsText.length && /[\d.]/.test(argsText[pos + 1]))) {
      const match = argsText.slice(pos).match(/^-?(?:\d+\.\d+|\.\d+|\d+)(deg|rad|pi|%)?/);
      if (match) {
        const numStr = match[0];
        const unitMatch = numStr.match(/(deg|rad|pi|%)$/);
        const unit = unitMatch ? unitMatch[1] as 'deg' | 'rad' | 'pi' | '%' : undefined;
        const valueStr = unit ? numStr.slice(0, -unit.length) : numStr;
        args.push({ type: 'NumberLiteral', value: parseFloat(valueStr), unit });
        pos += numStr.length;
        continue;
      }
    }

    // Boolean
    if (argsText.slice(pos).startsWith('true')) {
      args.push({ type: 'BooleanLiteral', value: true });
      pos += 4;
      continue;
    }
    if (argsText.slice(pos).startsWith('false')) {
      args.push({ type: 'BooleanLiteral', value: false });
      pos += 5;
      continue;
    }

    // Identifier (possibly followed by member access, function call, index access)
    if (/[a-zA-Z_]/.test(ch)) {
      const idMatch = argsText.slice(pos).match(/^[a-zA-Z_]\w*/);
      if (idMatch) {
        const name = idMatch[0];
        const argLoc = offsetToLoc(baseOffset + pos, source);
        pos += name.length;

        // Check for calc(...)
        if (name === 'calc' && pos < argsText.length && argsText[pos] === '(') {
          const parenContent = extractParenContent(argsText, pos);
          if (parenContent !== null) {
            // Parse the inner expression with location adjusted to source position
            const calcInnerOffset = baseOffset + pos + 1; // +1 for opening paren
            const innerExpr = parseExpressionAt(parenContent, calcInnerOffset, source);
            if (!innerExpr) {
              // Point at the first thing the expression grammar rejects rather
              // than treating the whole text as a variable name (which used to
              // surface as "Undefined variable: switch(k) { … }" at 1:1).
              const errLoc = offsetToLoc(calcInnerOffset + (_expressionParser?.errorOffset?.(parenContent) ?? 0), source);
              throw new Error(
                `Parse error at line ${errLoc.line}, column ${errLoc.column}: cannot parse the expression inside calc() — check for an unclosed brace or bracket`,
              );
            }
            // argLoc is where `calc` starts — a warning or error about the value
            // this argument produced points here (it used to have no position).
            args.push({ type: 'CalcExpression', expression: innerExpr, loc: argLoc });
            pos += parenContent.length + 2;
            continue;
          }
        }

        // Check for function call name(...)
        if (pos < argsText.length && argsText[pos] === '(') {
          const parenContent = extractParenContent(argsText, pos);
          if (parenContent !== null) {
            // Parse function args
            const fnArgs = parseFunctionArgs(parenContent, baseOffset + pos + 1, source);
            args.push({
              type: 'FunctionCall',
              name,
              args: fnArgs,
              loc: argLoc,
            } as FunctionCall);
            pos += parenContent.length + 2;
            continue;
          }
        }

        // Build identifier, then check for member/index chains
        const chained = parsePostfixChain({ type: 'Identifier', name, loc: argLoc } as Identifier, argsText, pos, baseOffset, source);
        args.push(chained.expr);
        pos = chained.pos;
        continue;
      }
    }

    // Array literal (`f([5, 6])`, `[1, 2][0]`): the tokenizer carries the
    // brackets intact, so hand the whole span to the real expression parser,
    // which already handles nesting, spread, and negative elements.
    if (ch === '[') {
      const bracketContent = extractBracketContent(argsText, pos);
      if (bracketContent !== null) {
        const literal = parseExpressionAt(`[${bracketContent}]`, baseOffset + pos, source);
        const start = pos;
        pos += bracketContent.length + 2;
        if (literal) {
          const chained = parsePostfixChain(literal as PathArg, argsText, pos, baseOffset, source);
          args.push(chained.expr);
          pos = chained.pos;
        } else {
          pos = start + bracketContent.length + 2;
        }
        continue;
      }
    }

    // Color literal
    if (ch === '#') {
      const colorMatch = argsText.slice(pos).match(/^#[0-9a-fA-F]+/);
      if (colorMatch) {
        // Colors in path args aren't standard but handle gracefully
        args.push({ type: 'NumberLiteral', value: 0 });
        pos += colorMatch[0].length;
        continue;
      }
    }

    // Skip unknown characters
    pos++;
  }

  return args;
}

/**
 * Fold `.prop`, `.method(args)`, and `[index]` suffixes onto a path-arg
 * expression. Shared by the identifier and array-literal branches of
 * parsePathArgs so both chain the same way. Returns the expression and the
 * position of the first unconsumed character.
 */
function parsePostfixChain(
  base: PathArg,
  argsText: string,
  pos: number,
  baseOffset: number,
  source: string,
): { expr: PathArg; pos: number } {
  let expr: PathArg = base;
  while (pos < argsText.length) {
    if (argsText[pos] === '.') {
      // Member access or method call
      const propMatch = argsText.slice(pos + 1).match(/^[a-zA-Z_]\w*/);
      if (!propMatch) break;
      const propName = propMatch[0];
      pos += 1 + propName.length;

      // Check if followed by (args) — method call
      if (pos < argsText.length && argsText[pos] === '(') {
        const parenContent = extractParenContent(argsText, pos);
        if (parenContent !== null) {
          const methodArgs = parseFunctionArgs(parenContent, baseOffset + pos + 1, source);
          expr = {
            type: 'MethodCallExpression',
            object: expr as Expression,
            method: propName,
            args: methodArgs,
            loc: (expr as { loc?: SourceLocation }).loc,
          } as MethodCallExpression;
          pos += parenContent.length + 2;
          continue;
        }
      }

      expr = {
        type: 'MemberExpression',
        object: expr as Expression,
        property: propName,
        loc: (expr as { loc?: SourceLocation }).loc,
      } as MemberExpression;
    } else if (argsText[pos] === '[') {
      // Index access
      const bracketContent = extractBracketContent(argsText, pos);
      if (bracketContent === null) break;
      const indexArgs = parsePathArgs(bracketContent, baseOffset + pos + 1, source);
      const indexExpr = indexArgs.length > 0 ? indexArgs[0] : { type: 'NumberLiteral', value: 0 } as NumberLiteral;
      expr = {
        type: 'IndexExpression',
        object: expr as Expression,
        index: indexExpr as Expression,
        loc: (expr as { loc?: SourceLocation }).loc,
      } as IndexExpression;
      pos += bracketContent.length + 2;
    } else {
      break;
    }
  }
  return { expr, pos };
}

/**
 * Parse comma-separated function arguments from a string.
 */
function parseFunctionArgs(argsStr: string, baseOffset: number, source: string): Expression[] {
  if (!argsStr.trim()) return [];

  // Split on commas at depth 0 (a comma inside a quoted string is content)
  const argStrings: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < argsStr.length; i++) {
    const afterString = skipQuoted(argsStr, i);
    if (afterString !== i) {
      i = afterString - 1;
      continue;
    }
    if (argsStr[i] === '(' || argsStr[i] === '[') depth++;
    else if (argsStr[i] === ')' || argsStr[i] === ']') depth--;
    else if (argsStr[i] === ',' && depth === 0) {
      argStrings.push(argsStr.slice(start, i).trim());
      start = i + 1;
    }
  }
  argStrings.push(argsStr.slice(start).trim());

  // Parse each argument as a path arg (which handles numbers, identifiers, member chains)
  const result: Expression[] = [];
  let offset = 0;
  for (const argStr of argStrings) {
    if (!argStr) continue;
    const parsed = parsePathArgs(argStr, baseOffset + offset, source);
    if (parsed.length > 0) {
      result.push(parsed[0] as Expression);
    }
    offset += argStr.length + 1; // +1 for comma
  }
  return result;
}

/**
 * If `text[pos]` opens a quoted string literal, return the index just past
 * its closing quote (or `text.length` when unterminated); otherwise `pos`.
 * The path-arg helpers below use it so a `)` `]` or `,` inside a string
 * argument (`calc(ease('sine-in', t))`, `Color('#fff')`) never counts as
 * structure — the greedy tokenizer already carries such strings intact.
 */
function skipQuoted(text: string, pos: number): number {
  const quote = text[pos];
  if (quote !== "'" && quote !== '"') return pos;
  let i = pos + 1;
  while (i < text.length && text[i] !== quote) {
    if (text[i] === '\\') i++;
    i++;
  }
  return Math.min(i + 1, text.length);
}

function extractBracketContent(text: string, openPos: number): string | null {
  if (text[openPos] !== '[') return null;
  let depth = 1;
  let pos = openPos + 1;
  while (pos < text.length && depth > 0) {
    const afterString = skipQuoted(text, pos);
    if (afterString !== pos) {
      pos = afterString;
      continue;
    }
    if (text[pos] === '[') depth++;
    else if (text[pos] === ']') depth--;
    pos++;
  }
  if (depth !== 0) return null;
  return text.slice(openPos + 1, pos - 1);
}

function extractParenContent(text: string, openPos: number): string | null {
  if (text[openPos] !== '(') return null;
  let depth = 1;
  let pos = openPos + 1;
  while (pos < text.length && depth > 0) {
    const afterString = skipQuoted(text, pos);
    if (afterString !== pos) {
      pos = afterString;
      continue;
    }
    if (text[pos] === '(') depth++;
    else if (text[pos] === ')') depth--;
    pos++;
  }
  if (depth !== 0) return null;
  return text.slice(openPos + 1, pos - 1);
}

function buildLayerDefinition(cursor: TreeCursor, source: string): LayerDefinition {
  const nodeLoc = loc(cursor, source);
  let layerType: 'PathLayer' | 'TextLayer' | 'GroupLayer' = 'PathLayer';
  let isDefault = false;
  let name: Expression = { type: 'StringLiteral', value: '' };
  let styleExpr: Expression = { type: 'StyleBlockLiteral', properties: [] };

  cursor.firstChild();
  let foundLayerType = false;
  let foundName = false;
  do {
    if (cursor.name === 'default') isDefault = true;
    else if (cursor.name === 'LayerType') {
      layerType = text(cursor, source) as 'PathLayer' | 'TextLayer' | 'GroupLayer';
      foundLayerType = true;
    } else if (foundLayerType && !foundName && isExpressionNode(cursor.name)) {
      // Postfix-aware: PathLayer(names[i]) / TextLayer(o.n).
      name = buildExpressionWithPostfix(cursor, source);
      foundName = true;
    } else if (foundName && isExpressionNode(cursor.name)) {
      // Postfix-aware too (review finding): define PathLayer('x')
      // cfg.style; flattened the same way the name did.
      styleExpr = buildExpressionWithPostfix(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'LayerDefinition', layerType, name, isDefault, styleExpr, loc: nodeLoc };
}

function buildViewBoxDefinition(cursor: TreeCursor, source: string): ViewBoxDefinition {
  const nodeLoc = loc(cursor, source);
  const args: Expression[] = [];

  cursor.firstChild();
  do {
    if (isExpressionNode(cursor.name)) {
      // Postfix-aware: ViewBox(0, 0, sheet.w, sizes[0]).
      args.push(buildExpressionWithPostfix(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();

  const zero: Expression = { type: 'NumberLiteral', value: 0 };
  return {
    type: 'ViewBoxDefinition',
    originX: args[0] ?? zero,
    originY: args[1] ?? zero,
    width: args[2] ?? zero,
    height: args[3] ?? zero,
    loc: nodeLoc,
  };
}

function buildLayerApplyBlock(cursor: TreeCursor, source: string): LayerApplyBlock {
  const nodeLoc = loc(cursor, source);
  let layerName: Expression = { type: 'StringLiteral', value: '' };
  let body: Statement[] = [];

  cursor.firstChild();
  let foundApply = false;
  do {
    if (cursor.name === 'apply') foundApply = true;
    else if (!foundApply && isExpressionNode(cursor.name) && cursor.name !== 'layer') {
      // Postfix-aware: layer(names[i]) / layer(o.n) / layer(pick(i)).
      // Plain buildExpression grabbed the base and the trailing postfix
      // fragments then OVERWROTE the target (layer(o.n) errored
      // "Undefined variable: n").
      layerName = buildExpressionWithPostfix(cursor, source);
      // In the paren-less `variable.apply { }` form the walker swallows
      // `.apply` as a member access — unwrap it and mark apply found.
      if (layerName.type === 'MemberExpression' && (layerName as MemberExpression).property === 'apply') {
        layerName = (layerName as MemberExpression).object;
        foundApply = true;
      }
      // The walker rests on the first unconsumed token — which here can
      // be the Block itself; grab it before the loop advances past it.
      if ((cursor.name as string) === 'Block') {
        body = atLoopBoundary(() => buildBlock(cursor, source));
      }
    } else if (cursor.name === 'Block') {
      body = atLoopBoundary(() => buildBlock(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'LayerApplyBlock', layerName, body, loc: nodeLoc };
}

function buildTextStatement(cursor: TreeCursor, source: string): TextStatement {
  const nodeLoc = loc(cursor, source);
  let x: Expression = { type: 'NumberLiteral', value: 0 };
  let y: Expression = { type: 'NumberLiteral', value: 0 };
  let rotation: Expression | undefined;
  let styles: Expression | undefined;
  let content: TemplateLiteral | undefined;
  let body: TextBodyItem[] | undefined;

  // Extract expressions between parens, then template or block. Each argument
  // can be any expression — including function calls like `polarX(...)`,
  // method calls like `pt.x`, and member-chains like `p.point.x` — so we
  // delegate to buildExpressionWithPostfix which walks the postfix chain
  // (ArgList, ".", "[") at sibling level.
  cursor.firstChild();
  const exprs: Expression[] = [];
  let inParens = false;
  do {
    if (cursor.name === '(') { inParens = true; continue; }
    if (cursor.name === ')') { inParens = false; continue; }
    if (inParens && cursor.name === ',') continue;
    if (inParens && isExpressionNode(cursor.name)) {
      const expr = buildExpressionWithPostfix(cursor, source);
      // buildExpressionWithPostfix leaves the cursor on the last token
      // consumed; the outer do/while's nextSibling() advances from there.
      exprs.push(expr);
      // Sync the inParens flag if we're now at a ")"
      if (cursor.name === ')') inParens = false;
      continue;
    }
    if (!inParens && cursor.name === 'TemplateLiteral') {
      content = buildTemplateLiteral(cursor, source);
    } else if (!inParens && cursor.name === 'TextBlock') {
      body = atLoopBoundary(() => buildTextBlock(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();

  if (exprs.length >= 1) x = exprs[0];
  if (exprs.length >= 2) y = exprs[1];
  if (exprs.length === 3 && exprs[2].type === 'StyleBlockLiteral') {
    // text(x, y, #{ … }) — a style block with no rotation to skip past.
    styles = exprs[2];
  } else {
    if (exprs.length >= 3) rotation = exprs[2];
    if (exprs.length >= 4) styles = exprs[3];
  }

  return { type: 'TextStatement', x, y, rotation, styles, content, body, loc: nodeLoc };
}

function buildTspanStatement(cursor: TreeCursor, source: string): TspanStatement {
  const nodeLoc = loc(cursor, source);
  let dx: Expression | undefined;
  let dy: Expression | undefined;
  let rotation: Expression | undefined;
  let styles: Expression | undefined;
  let content: TemplateLiteral = { type: 'TemplateLiteral', parts: [] };

  cursor.firstChild();
  const args: Expression[] = [];
  let inParens = false;
  do {
    if (cursor.name === '(') { inParens = true; continue; }
    if (cursor.name === ')') { inParens = false; continue; }
    if (inParens && cursor.name === ',') continue;
    if (inParens && isExpressionNode(cursor.name)) {
      // Delegate to buildExpressionWithPostfix so each argument can be any
      // expression — function calls (`polarX(...)`), member chains
      // (`pt.x`), method calls, etc. — not just a bare primary.
      const expr = buildExpressionWithPostfix(cursor, source);
      args.push(expr);
      if (cursor.name === ')') inParens = false;
      continue;
    }
    if (!inParens && cursor.name === 'TemplateLiteral') {
      content = buildTemplateLiteral(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  if (args.length >= 1) dx = args[0];
  if (args.length >= 2) dy = args[1];
  if (args.length >= 3) rotation = args[2];
  if (args.length >= 4) styles = args[3];

  return { type: 'TspanStatement', dx, dy, rotation, styles, content, loc: nodeLoc };
}

// Single dispatcher for every text-body position (TextBlock, and the inline
// bodies of text for/foreach/if/switch). Text blocks contain text-specific
// nodes (TextForLoop, TextIfStatement, …) as well as regular statements
// (LetDeclaration, TspanStatement). One dispatcher means a new text
// construct is either handled everywhere or nowhere — the previous per-site
// copies silently dropped nested loops/ifs in some bodies.
function buildTextBodyItem(cursor: TreeCursor, source: string): TextBodyItem | null {
  switch (cursor.name) {
    case 'TspanStatement': return buildTspanStatement(cursor, source);
    case 'TemplateLiteral': return buildTemplateLiteral(cursor, source) as TextBodyItem;
    case 'TextForLoop': return buildTextForLoop(cursor, source);
    case 'TextForEachLoop': return buildTextForEachLoop(cursor, source);
    case 'TextIfStatement': return buildTextIfStatement(cursor, source);
    case 'TextSwitchStatement': return buildTextSwitchStatement(cursor, source);
    default: return buildStatement(cursor, source) as TextBodyItem | null;
  }
}

// Consume an inline text body: the cursor is on '{'; items are collected
// until the matching '}', which the cursor rests on afterwards.
function buildInlineTextBody(cursor: TreeCursor, source: string): TextBodyItem[] {
  const items: TextBodyItem[] = [];
  // Raw stepping: a comment between items is kept as a Comment item
  while (nextSiblingRaw(cursor) && cursor.name !== '}') {
    const item = buildTextBodyItem(cursor, source);
    if (item) items.push(item);
  }
  return items;
}

function buildTextBlock(cursor: TreeCursor, source: string): TextBodyItem[] {
  const items: TextBodyItem[] = [];
  firstChildRaw(cursor);
  do {
    if (cursor.name === '{' || cursor.name === '}') continue;
    const item = buildTextBodyItem(cursor, source);
    if (item) items.push(item);
  } while (nextSiblingRaw(cursor));
  cursor.parent();
  return items;
}

function buildTextForLoop(cursor: TreeCursor, source: string): ForLoop {
  const nodeLoc = loc(cursor, source);
  let variable = '';
  let start: Expression = { type: 'NumberLiteral', value: 0 };
  let end: Expression = { type: 'NumberLiteral', value: 0 };
  const body: Statement[] = [];
  let inclusive = true;

  cursor.firstChild();
  let phase = 0;
  const noteRangeOp = (name: string): boolean => {
    if (name !== 'RangeOp' && name !== 'HalfOpenRangeOp') return false;
    inclusive = name === 'RangeOp';
    phase = 2;
    return true;
  };
  do {
    if (cursor.name === 'VariableName') {
      variable = text(cursor, source);
    } else if (noteRangeOp(cursor.name)) {
      // range operator handled
    } else if (phase === 0 && isExpressionNode(cursor.name) && cursor.name !== 'for' && cursor.name !== 'in') {
      // Postfix-aware for the same reason as buildForLoop — and the same
      // range-operator cursor-rest check (see there).
      start = buildExpressionWithPostfix(cursor, source);
      if (!noteRangeOp(cursor.name as string)) phase = 1;
    } else if (phase === 2 && isExpressionNode(cursor.name)) {
      end = buildExpressionWithPostfix(cursor, source);
    } else if (cursor.name === '{') {
      // Collect text body items (a loop body — break/continue become valid)
      loopDepth++;
      try {
        body.push(...(buildInlineTextBody(cursor, source) as Statement[]));
      } finally {
        loopDepth--;
      }
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'ForLoop', variable, start, end, inclusive, body, loc: nodeLoc };
}

function buildTextForEachLoop(cursor: TreeCursor, source: string): ForEachLoop {
  const nodeLoc = loc(cursor, source);
  let variable = '';
  let indexVariable: string | undefined;
  let iterable: Expression = { type: 'NullLiteral' };
  const body: Statement[] = [];

  cursor.firstChild();
  let foundIn = false;
  do {
    if (cursor.name === 'VariableName' && !foundIn) variable = text(cursor, source);
    else if (cursor.name === 'ForEachDestructure') {
      const vars = extractVariableNames(cursor, source);
      if (vars.length >= 1) variable = vars[0];
      if (vars.length >= 2) indexVariable = vars[1];
    } else if (cursor.name === 'in') foundIn = true;
    else if (foundIn && isExpressionNode(cursor.name) && cursor.name !== ')') {
      iterable = buildExpressionWithPostfix(cursor, source);
    } else if (cursor.name === '{') {
      // Loop body — break/continue become valid
      loopDepth++;
      try {
        body.push(...(buildInlineTextBody(cursor, source) as Statement[]));
      } finally {
        loopDepth--;
      }
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'ForEachLoop', variable, indexVariable, iterable, body, loc: nodeLoc };
}

function buildTextIfStatement(cursor: TreeCursor, source: string): IfStatement {
  const nodeLoc = loc(cursor, source);
  let condition: Expression = { type: 'BooleanLiteral', value: true };
  let consequent: Statement[] = [];
  let alternate: Statement[] | null = null;

  cursor.firstChild();
  do {
    if (cursor.name === '(') {
      const condStart = cursor.to;
      let condEnd = condStart;
      while (cursor.nextSibling()) {
        if (cursor.name === ')') { condEnd = cursor.from; break; }
      }
      const condStr = source.slice(condStart, condEnd).trim();
      if (condStr) {
        const parsed = parseExpressionString(condStr);
        if (parsed) condition = parsed;
      }
    } else if (cursor.name === '{') {
      // Text block body — collect items until }
      const bodyItems = buildInlineTextBody(cursor, source);
      if (consequent.length === 0 && !alternate) {
        consequent = bodyItems as Statement[];
      } else {
        alternate = bodyItems as Statement[];
      }
    } else if (cursor.name === 'else') {
      // Next should be { or TextIfStatement
    } else if (cursor.name === 'TextIfStatement') {
      alternate = [buildTextIfStatement(cursor, source)];
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'IfStatement', condition, consequent, alternate, loc: nodeLoc };
}

function buildFontDirective(cursor: TreeCursor, source: string): FontDirective {
  const nodeLoc = loc(cursor, source);
  const directiveFrom = cursor.from;
  let fontSource = '';
  let sourceKind: FontDirective['sourceKind'];
  let weight: number | undefined;

  cursor.firstChild();
  do {
    if (cursor.name === 'String') {
      fontSource = parseStringContent(text(cursor, source));
    } else if (cursor.name === 'Identifier') {
      // `@font` is a single token, so `@fontFamily` would otherwise tokenize
      // as `@font` + `Family`. Require whitespace before an identifier source.
      if (cursor.from === directiveFrom + '@font'.length) {
        const typed = source.slice(directiveFrom, cursor.to);
        // `@fontFamily` was most likely meant as `@font fontFamily` — suggest
        // the full typed name (minus `@`), not the `Family` remainder token.
        throw new Error(
          `Parse error at line ${nodeLoc.line}, column ${nodeLoc.column}: unknown directive '${typed}' — did you mean '@font ${typed.slice(1)}'?`,
        );
      }
      fontSource = text(cursor, source);
      sourceKind = 'identifier';
    } else if (cursor.name === 'Number') {
      weight = parseFloat(text(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'FontDirective', source: fontSource, sourceKind, weight, loc: nodeLoc };
}

// --- Expression builders ---

function buildExpression(cursor: TreeCursor, source: string): Expression {
  switch (cursor.name) {
    case 'TernaryExpression': return buildTernaryExpression(cursor, source);
    case 'SwitchExpression': return buildSwitchExpression(cursor, source);
    case 'BinaryExpression': return buildBinaryExpression(cursor, source);
    case 'UnaryExpression': return buildUnaryExpression(cursor, source);
    case 'Number': return buildNumberLiteral(cursor, source);
    case 'String': return buildStringLiteral(cursor, source);
    case 'TemplateLiteral': return buildTemplateLiteral(cursor, source);
    case 'BooleanLiteral': return buildBooleanLiteral(cursor, source);
    case 'NullLiteral': return { type: 'NullLiteral', loc: loc(cursor, source) };
    case 'ColorLiteral': return buildColorLiteral(cursor, source);
    case 'CSSColorLiteral': return buildColorLiteral(cursor, source);
    case 'ArrayLiteral': return buildArrayLiteral(cursor, source);
    case 'ObjectLiteral': return buildObjectLiteral(cursor, source);
    case 'StyleBlockLiteral': return buildStyleBlockLiteral(cursor, source);
    case 'PathBlockExpression': return buildPathBlockExpression(cursor, source);
    case 'TextBlockExpression': return buildTextBlockExpression(cursor, source);
    case 'CalcExpression': return buildCalcExpression(cursor, source);
    case 'LayerConstructor': return buildLayerConstructor(cursor, source);
    case 'LayerCallExpression': return buildLayerCallExpression(cursor, source);
    case 'Identifier': return buildIdentifier(cursor, source);
    case 'ParenExpression': return buildParenExpression(cursor, source);
    case 'RangeExpression': return buildRangeExpression(cursor, source);
    // A TrailingBlock reached as an expression (not as a call suffix — those are
    // consumed by explicit peeks in buildExpressionWithPostfix) is a lambda literal.
    case 'TrailingBlock': {
      const nodeLoc = loc(cursor, source);
      const { params, body } = buildTrailingBlock(cursor, source);
      return { type: 'LambdaExpression', params, body, loc: nodeLoc };
    }
    case 'ArgList': return { type: 'NullLiteral' }; // ArgList is a postfix, handled in context
    default:
      // For postfix expressions, walk the chain
      if (cursor.name === 'postfixExpression' || isExpressionNode(cursor.name)) {
        return buildPostfixExpression(cursor, source);
      }
      return { type: 'Identifier', name: text(cursor, source), loc: loc(cursor, source) };
  }
}

/**
 * Build an expression, then check for postfix operations at the sibling level.
 * Handles: Identifier followed by ArgList, ".", "[" as siblings.
 * For compound nodes (BinaryExpression, etc.), just delegates to buildExpression.
 */
function buildExpressionWithPostfix(cursor: TreeCursor, source: string): Expression {
  // If the node is a compound expression, just build it directly
  const name = cursor.name;
  // These compound expressions never have postfix ops at the sibling level
  if (name === 'BinaryExpression' || name === 'TernaryExpression' ||
      name === 'UnaryExpression' || name === 'CalcExpression') {
    return buildExpression(cursor, source);
  }

  // For primary nodes (Identifier, Number, etc.), build and check for postfix siblings
  let expr = buildExpression(cursor, source);

  // Walk subsequent siblings for postfix ops.
  // Key invariant: after processing each op, the cursor must be on the LAST
  // token consumed by that op. The while loop's nextSibling() then advances
  // to the next unprocessed token. When an op peeks ahead and finds something
  // it doesn't consume, we use `needsAdvance = false` to skip the next
  // nextSibling() call.
  let needsAdvance = true;
  while (needsAdvance ? cursor.nextSibling() : true) {
    needsAdvance = true; // Reset for next iteration
    const sibName = cursor.name;

    if (sibName === 'ArgList') {
      const args = buildArgList(cursor, source);
      let block: { params: string[]; body: Statement[] } | undefined;
      // Peek for TrailingBlock
      if (cursor.nextSibling()) {
        if (cursor.name === 'TrailingBlock') {
          block = buildTrailingBlock(cursor, source);
        } else {
          needsAdvance = false; // Don't advance — cursor is on next unprocessed token
        }
      }
      if (expr.type === 'Identifier') {
        expr = { type: 'FunctionCall', name: expr.name, args, block, loc: (expr as Identifier).loc } as FunctionCall;
      }
    } else if (sibName === '.') {
      if (!cursor.nextSibling()) break;

      // Handle CSSColorLiteral after dot (e.g., Color.oklch(...) tokenized as one token)
      if (cursor.name === 'CSSColorLiteral' || cursor.name === '⚠') {
        const raw = text(cursor, source);
        // Extract method name and args from e.g., "oklch(0.7, 0.15, 200)".
        // Balanced-paren aware, so nested calls like `mix(a, b)` in an argument
        // are not truncated the way the old `[^)]*` capture was.
        const fnMatch = matchFunctionNotation(raw);
        if (fnMatch && /^[A-Za-z_]/.test(fnMatch.name)) {
          const methodArgs = parseFunctionArgs(fnMatch.args, cursor.from + fnMatch.name.length + 1, source);
          expr = { type: 'MethodCallExpression', object: expr, method: fnMatch.name, args: methodArgs, loc: (expr as { loc?: SourceLocation }).loc } as MethodCallExpression;
        } else {
          expr = {
            type: 'MemberExpression',
            object: expr,
            property: raw,
            loc: (expr as { loc?: SourceLocation }).loc,
          } as MemberExpression;
        }
        // Continue — cursor is on the CSSColorLiteral, next iteration will advance
        continue;
      }

      const propName = text(cursor, source);

      // Peek ahead for ArgList, TrailingBlock, or next op
      if (!cursor.nextSibling()) {
        expr = {
          type: 'MemberExpression',
          object: expr,
          property: propName,
          loc: (expr as { loc?: SourceLocation }).loc,
        } as MemberExpression;
        break;
      }

      if (cursor.name === 'ArgList') {
        const args = buildArgList(cursor, source);
        let block: { params: string[]; body: Statement[] } | undefined;
        if (cursor.nextSibling()) {
          if (cursor.name === 'TrailingBlock') {
            block = buildTrailingBlock(cursor, source);
          } else {
            needsAdvance = false;
          }
        }
        expr = { type: 'MethodCallExpression', object: expr, method: propName, args, block, loc: (expr as { loc?: SourceLocation }).loc } as MethodCallExpression;
      } else if (cursor.name === 'TrailingBlock') {
        const block = buildTrailingBlock(cursor, source);
        expr = { type: 'MethodCallExpression', object: expr, method: propName, args: [], block, loc: (expr as { loc?: SourceLocation }).loc } as MethodCallExpression;
      } else {
        // Simple member access — cursor is already on the next token
        expr = {
          type: 'MemberExpression',
          object: expr,
          property: propName,
          loc: (expr as { loc?: SourceLocation }).loc,
        } as MemberExpression;
        needsAdvance = false; // Don't skip the current token
      }
    } else if (sibName === '[') {
      if (!cursor.nextSibling()) break;
      // The interior may itself be a postfix chain (arr[o.n],
      // arr[pick()], arr[idx[0]]) spanning siblings; the recursive
      // build rests ON ']' (its break set), so only skip when it
      // stopped short of it.
      const index = buildExpressionWithPostfix(cursor, source);
      if ((cursor.name as string) !== ']') cursor.nextSibling(); // skip ']'
      expr = {
        type: 'IndexExpression',
        object: expr,
        index,
        loc: (expr as { loc?: SourceLocation }).loc,
      } as IndexExpression;
    } else if (sibName === 'TrailingBlock') {
      const block = buildTrailingBlock(cursor, source);
      if (expr.type === 'FunctionCall') {
        (expr as FunctionCall).block = block;
      }
    } else if (sibName === ';' || sibName === '⚠' || sibName === '=' || sibName === ',' || sibName === ')' || sibName === ']' ||
               sibName === '==' || sibName === '!=' || sibName === '<' || sibName === '>' ||
               sibName === '<=' || sibName === '>=' || sibName === '||' || sibName === '&&' ||
               sibName === '<<' || sibName === '+' || sibName === '-' || sibName === '*' ||
               sibName === '/' || sibName === '%' || sibName === '?' || sibName === ':') {
      break;
    } else {
      break;
    }
  }

  return expr;
}

function buildTrailingBlock(
  cursor: TreeCursor,
  source: string,
): { params: string[]; body: Statement[]; loc: SourceLocation } {
  const params: string[] = [];
  const body: Statement[] = [];
  // Where the block itself starts (its `{`), so tools can tell its parameters
  // from same-named ones in the call's arguments.
  const blockLoc = loc(cursor, source);

  // Lambda / callback bodies are break/continue boundaries
  const savedLoopDepth = loopDepth;
  loopDepth = 0;
  try {
    firstChildRaw(cursor); // Enter TrailingBlock (raw: body comments are kept)
    let inParams = false;
    let passedParams = false;
    do {
      if (cursor.name === '||') {
        // Zero-param form {|| ... } — both pipes lex as one logical-or token
        passedParams = true;
      } else if (cursor.name === '|' && !inParams) {
        inParams = true;
      } else if (cursor.name === '|' && inParams) {
        inParams = false;
        passedParams = true;
      } else if (inParams && (cursor.name === 'VariableName' || cursor.name === 'Identifier')) {
        params.push(text(cursor, source));
      } else if (passedParams && cursor.name !== '{' && cursor.name !== '}') {
        if (isExpressionNode(cursor.name)) {
          // Expression-bodied sugar {|v| v * 2}: the grammar admits one
          // trailing bare expression — wrap it in an implicit return.
          body.push({
            type: 'ReturnStatement',
            value: buildExpressionWithPostfix(cursor, source),
            implicit: true,
          });
        } else {
          const stmt = buildStatement(cursor, source);
          if (stmt) body.push(stmt);
        }
      }
    } while (nextSiblingRaw(cursor));
    cursor.parent();
  } finally {
    loopDepth = savedLoopDepth;
  }

  // An expression-bodied block `{|v| v * 2}` prints inline and has no line to
  // hang a comment on: leave its comments out of the AST, so the formatter
  // sees them as uncaptured and keeps the enclosing statement verbatim.
  if (body.some((stmt) => stmt.type === 'ReturnStatement' && stmt.implicit)) {
    for (let i = body.length - 1; i >= 0; i--) {
      if (body[i].type === 'Comment') body.splice(i, 1);
    }
  }

  return { params, body, loc: blockLoc };
}

function buildPostfixExpression(cursor: TreeCursor, source: string): Expression {
  // CURRENTLY UNREACHABLE (verified 2026-08-26 via v8 coverage over the
  // full suite: 0 hits). Its only dispatch — buildExpression's default
  // case matching 'postfixExpression' — can never fire: the grammar rule
  // is lowercase (Lezer inlines it), and every isExpressionNode name has
  // an earlier explicit case. Kept mirroring buildExpressionWithPostfix
  // (including the postfix-aware '[' interior) so it stays correct if a
  // grammar change ever surfaces the node; candidate for deletion in a
  // parser cleanup.
  // A postfix expression is: primaryExpression followed by postfix ops
  // Walk through children building up the expression chain
  cursor.firstChild();
  let expr = buildExpression(cursor, source);

  while (cursor.nextSibling()) {
    const name = cursor.name;
    if (name === '.') {
      // Member access: expr.property or method call: expr.method(args)
      if (cursor.nextSibling()) {
        const propName = text(cursor, source);
        // Check if followed by ArgList
        if (cursor.nextSibling() && cursor.name === 'ArgList') {
          // Method call
          const args = buildArgList(cursor, source);
          expr = {
            type: 'MethodCallExpression',
            object: expr,
            method: propName,
            args,
            loc: (expr as { loc?: SourceLocation }).loc,
          } as MethodCallExpression;
        } else {
          // Simple member access
          expr = {
            type: 'MemberExpression',
            object: expr,
            property: propName,
            loc: (expr as { loc?: SourceLocation }).loc,
          } as MemberExpression;
          // We consumed too far — the cursor is on the next thing
          continue;
        }
      }
    } else if (name === 'ArgList') {
      // Function call: expr(args)
      const args = buildArgList(cursor, source);
      if (expr.type === 'Identifier') {
        expr = {
          type: 'FunctionCall',
          name: expr.name,
          args,
          loc: (expr as Identifier).loc,
        } as FunctionCall;
      }
    } else if (name === '[') {
      // Index access: expr[index] — postfix-aware interior, same
      // rationale and ']'-rest handling as buildExpressionWithPostfix.
      if (cursor.nextSibling()) {
        const index = buildExpressionWithPostfix(cursor, source);
        if ((cursor.name as string) !== ']') cursor.nextSibling(); // skip ']'
        expr = {
          type: 'IndexExpression',
          object: expr,
          index,
          loc: (expr as { loc?: SourceLocation }).loc,
        } as IndexExpression;
      }
    } else if (name === ';' || name === '⚠') {
      break;
    }
  }
  cursor.parent();
  return expr;
}

function buildArgList(cursor: TreeCursor, source: string): Expression[] {
  const args: Expression[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === '(' || cursor.name === ')') continue;
    if (cursor.name === ',') continue;
    // Each argument may include postfix ops (member access, index, calls)
    args.push(buildExpressionWithPostfix(cursor, source));
  } while (cursor.nextSibling());
  cursor.parent();
  return args;
}

function buildTernaryExpression(cursor: TreeCursor, source: string): TernaryExpression {
  cursor.firstChild();
  const parts: Expression[] = [];
  // Same postfix-folding rationale as buildUnaryExpression: any of the
  // three ternary operands can be a function call / member chain, and
  // bare `buildExpression` would drop the ArgList sibling.
  do {
    if (cursor.name !== '?' && cursor.name !== ':' && isExpressionNode(cursor.name)) {
      parts.push(buildExpressionWithPostfix(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return {
    type: 'TernaryExpression',
    condition: parts[0] || { type: 'BooleanLiteral', value: true },
    consequent: parts[1] || { type: 'NullLiteral' },
    alternate: parts[2] || { type: 'NullLiteral' },
  };
}

function buildBinaryExpression(cursor: TreeCursor, source: string): BinaryExpression {
  cursor.firstChild();
  let left: Expression = { type: 'NullLiteral' };
  let operator = '+';
  let right: Expression = { type: 'NullLiteral' };

  // Collect all children, find the operator, then build left and right
  const children: Array<{ name: string; from: number; to: number }> = [];
  do {
    children.push({ name: cursor.name, from: cursor.from, to: cursor.to });
  } while (cursor.nextSibling());
  cursor.parent();

  // Find the operator
  const opIdx = children.findIndex(c => isOperator(c.name));
  if (opIdx >= 0) {
    operator = source.slice(children[opIdx].from, children[opIdx].to);

    // Build left side: if it's a single expression node, use buildExpression.
    // If multiple nodes (postfix chain), extract as raw text.
    if (opIdx === 1) {
      // Single node before operator — use tree
      cursor.firstChild();
      left = buildExpression(cursor, source);
      cursor.parent();
    } else if (opIdx > 1) {
      // Multiple nodes — postfix chain. Use tree walking.
      cursor.firstChild();
      left = buildExpressionWithPostfix(cursor, source);
      cursor.parent();
    }

    // Build right side: everything after operator
    const rightChildren = children.slice(opIdx + 1);
    if (rightChildren.length === 1) {
      cursor.firstChild();
      // Skip to the right operand
      for (let i = 0; i <= opIdx; i++) cursor.nextSibling();
      right = buildExpression(cursor, source);
      cursor.parent();
    } else if (rightChildren.length > 1) {
      // Multiple nodes — use buildExpressionWithPostfix from the right start
      cursor.firstChild();
      for (let i = 0; i <= opIdx; i++) cursor.nextSibling();
      right = buildExpressionWithPostfix(cursor, source);
      cursor.parent();
    }
  }

  return {
    type: 'BinaryExpression',
    operator: operator as BinaryExpression['operator'],
    left,
    right,
  };
}

function buildUnaryExpression(cursor: TreeCursor, source: string): UnaryExpression {
  const nodeLoc = loc(cursor, source);
  cursor.firstChild();
  let operator: '-' | '!' = '-';
  let argument: Expression = { type: 'NullLiteral' };

  // Use buildExpressionWithPostfix so the unary's operand can be a
  // function call / member chain / index expression — not just a bare
  // primary. Without this, `-sin(0.5)` would parse `sin` as Identifier
  // and drop the ArgList, since `buildExpression` returns NullLiteral
  // for ArgList nodes (they're meant to be folded into a postfix chain).
  do {
    if (cursor.name === '-' || cursor.name === '!') {
      operator = text(cursor, source) as '-' | '!';
    } else if (isExpressionNode(cursor.name)) {
      argument = buildExpressionWithPostfix(cursor, source);
      break;
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'UnaryExpression', operator, argument, loc: nodeLoc };
}

// --- Literal builders ---

function buildNumberLiteral(cursor: TreeCursor, source: string): NumberLiteral {
  const raw = text(cursor, source);
  const unitMatch = raw.match(/(deg|rad|pi|%)$/);
  const unit = unitMatch ? unitMatch[1] as 'deg' | 'rad' | 'pi' | '%' : undefined;
  const valueStr = unit ? raw.slice(0, -unit.length) : raw;
  return { type: 'NumberLiteral', value: parseFloat(valueStr), unit, loc: loc(cursor, source) };
}

function buildStringLiteral(cursor: TreeCursor, source: string): StringLiteral {
  return { type: 'StringLiteral', value: parseStringContent(text(cursor, source)), loc: loc(cursor, source) };
}

function buildBooleanLiteral(cursor: TreeCursor, source: string): BooleanLiteral {
  return { type: 'BooleanLiteral', value: text(cursor, source) === 'true', loc: loc(cursor, source) };
}

function buildColorLiteral(cursor: TreeCursor, source: string): ColorLiteral {
  return { type: 'ColorLiteral', raw: text(cursor, source), loc: loc(cursor, source) };
}

function buildIdentifier(cursor: TreeCursor, source: string): Identifier {
  return { type: 'Identifier', name: text(cursor, source), loc: loc(cursor, source) };
}

function buildTemplateLiteral(cursor: TreeCursor, source: string): TemplateLiteral {
  // Walk the CST. The grammar parses `${...}` interpolations INLINE as real
  // expression subtrees under TemplateInterpolation nodes — correct even for
  // hard cases like a `}` inside a string argument (`${ f("}") }`). Only the
  // literal text runs are structural gaps (the lowercase template tokens are
  // not node types), and those are recovered by range between children.
  //
  // History: this function previously discarded the tree and re-scanned the
  // raw text (commits 8c89ff4/0041d3e, reacting to the missing text nodes) —
  // which silently mis-parsed interpolations containing braces in strings and
  // re-parsed every expression a second time with offset-rewritten locations.
  // The raw scanner survives only as the error-recovery fallback below.
  const nodeFrom = cursor.from;
  const nodeLoc = loc(cursor, source);
  const nodeTo = cursor.to;
  const raw = text(cursor, source);
  const opened = raw.startsWith('`');
  const closed = opened && raw.length >= 2 && raw.endsWith('`');
  // Unterminated templates (mid-typing, error recovery) have no closing
  // backtick — the literal content then runs to the node end.
  const contentEnd = closed ? nodeTo - 1 : nodeTo;

  const parts: (string | Expression)[] = [];
  let sawInterpolation = false;
  let prevEnd = nodeFrom + (opened ? 1 : 0);

  if (cursor.firstChild()) {
    do {
      if (cursor.name !== 'TemplateInterpolation') continue;
      sawInterpolation = true;
      // Literal gap before this interpolation (empty gaps push nothing —
      // matches the legacy parts contract for adjacent interpolations).
      if (cursor.from > prevEnd) {
        parts.push(unescapeTemplate(source.slice(prevEnd, cursor.from)));
      }
      let interpEnd = cursor.to;
      // Empty/mid-typing interpolation (`${}` — the auto-close intermediate
      // state on every keystroke): error recovery ends the node right after
      // `${`, leaving the `}` unconsumed — without this it would leak into
      // the following literal run. A well-formed interpolation's own span
      // already includes its `}`, so the end-check prevents double-consuming
      // a literal `}` that follows one (`` `${a}}` ``).
      if (!source.slice(cursor.from, cursor.to).endsWith('}') && source[interpEnd] === '}') {
        interpEnd += 1;
      }
      // The `${`/`}` delimiters are token-level (no nodes); the children are
      // the expression's node(s). Postfix operators (`(args)`, `.prop`,
      // `[idx]`) appear as SIBLINGS, so use the postfix-aware builder — same
      // pattern as buildCalcExpression.
      let expr: Expression | null = null;
      if (cursor.firstChild()) {
        do {
          if (isExpressionNode(cursor.name)) {
            expr = buildExpressionWithPostfix(cursor, source);
            break;
          }
        } while (cursor.nextSibling());
        cursor.parent();
      }
      // Mid-typing interpolation with no parsable expression contributes no
      // part (lenient) rather than a bogus Identifier.
      if (expr) parts.push(expr);
      prevEnd = interpEnd;
    } while (cursor.nextSibling());
    cursor.parent();
  }

  // Error-recovery fallback: the node has no structured interpolations but
  // its text contains `${` — an opaque/recovered shape (historically hit as
  // "TemplateLiteral with no children", commit 8c89ff4). Use the legacy raw
  // scanner so those degraded states still yield usable parts.
  if (!sawInterpolation && raw.includes('${') && opened) {
    const inner = closed ? raw.slice(1, -1) : raw.slice(1);
    return { type: 'TemplateLiteral', parts: parseTemplateString(inner, nodeFrom + 1, source), loc: nodeLoc };
  }

  // Trailing literal run (or the entire content for interpolation-free templates)
  if (contentEnd > prevEnd) {
    parts.push(unescapeTemplate(source.slice(prevEnd, contentEnd)));
  }
  return { type: 'TemplateLiteral', parts, loc: nodeLoc };
}

/**
 * Parse a template string's inner content (between backticks) into parts.
 * Handles ${expr} interpolations by re-parsing the expression content.
 *
 * DEGRADED PATH ONLY — kept for error-recovered/opaque TemplateLiteral nodes
 * (no TemplateInterpolation children). The brace counter here is NOT
 * string-aware (`${ f("}") }` mis-splits); the primary CST walk above never
 * has that problem because the grammar already parsed the expression.
 */
function parseTemplateString(inner: string, baseOffset: number, source: string): (string | Expression)[] {
  const parts: (string | Expression)[] = [];
  let pos = 0;
  let textStart = 0;

  while (pos < inner.length) {
    if (inner[pos] === '\\' && pos + 1 < inner.length) {
      pos += 2; // Skip escape sequence
      continue;
    }
    if (inner[pos] === '$' && pos + 1 < inner.length && inner[pos + 1] === '{') {
      // Emit preceding text (with escape processing)
      if (pos > textStart) {
        parts.push(unescapeTemplate(inner.slice(textStart, pos)));
      }
      // Find matching }
      let depth = 1;
      let end = pos + 2;
      while (end < inner.length && depth > 0) {
        if (inner[end] === '{') depth++;
        else if (inner[end] === '}') depth--;
        end++;
      }
      const exprStr = inner.slice(pos + 2, end - 1);
      // Parse the interpolation expression using Parsimmon
      const parsed = parseExpressionString(exprStr);
      parts.push(parsed || { type: 'Identifier', name: exprStr } as Identifier);
      pos = end;
      textStart = pos;
      continue;
    }
    pos++;
  }
  // Emit remaining text (with escape processing)
  if (pos > textStart) {
    parts.push(unescapeTemplate(inner.slice(textStart, pos)));
  }
  return parts;
}

function unescapeTemplate(s: string): string {
  // `\$` first: the grammar already keeps `\${` from opening an
  // interpolation, so here it only has to drop the backslash (`\${` → `${`).
  return s
    .replace(/\\\$/g, '$')
    .replace(/\\`/g, '`')
    .replace(/\\\\/g, '\\')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t');
}

function buildCalcExpression(cursor: TreeCursor, source: string): CalcExpression {
  const nodeLoc = loc(cursor, source);
  cursor.firstChild();
  let expression: Expression = { type: 'NullLiteral' };
  // The body of `calc(expr)` is a single expression. We must use
  // buildExpressionWithPostfix so postfix operators (`(args)`, `.prop`,
  // `[idx]`) get folded into the expression — otherwise `calc(foo(5))`
  // builds as Identifier("foo") and the ArgList sibling falls through to
  // the `buildExpression` case 'ArgList' branch which returns NullLiteral,
  // clobbering the identifier. Break after the first real expression so a
  // trailing `)` doesn't trigger that bug.
  do {
    if (cursor.name !== 'calc' && cursor.name !== '(' && cursor.name !== ')' && isExpressionNode(cursor.name)) {
      expression = buildExpressionWithPostfix(cursor, source);
      break;
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return { type: 'CalcExpression', expression, loc: nodeLoc };
}

function buildArrayLiteral(cursor: TreeCursor, source: string): ArrayLiteral {
  // A method call takes its error position from its receiver (ISSUE-022).
  const nodeLoc = loc(cursor, source);
  const elements: (Expression | SpreadElement)[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === 'SpreadElement') {
      cursor.firstChild();
      let arg: Expression = { type: 'NullLiteral' };
      do {
        // buildExpressionWithPostfix folds postfix operators (index, member,
        // call) into the argument so e.g. `...ramp[0]` parses as a spread
        // of an IndexExpression rather than leaking the `[0]` as siblings.
        if (cursor.name !== '...') arg = buildExpressionWithPostfix(cursor, source);
      } while (cursor.nextSibling());
      cursor.parent();
      elements.push({ type: 'SpreadElement', argument: arg });
    } else if (cursor.name !== '[' && cursor.name !== ']' && cursor.name !== ',') {
      // Use the postfix-aware builder so array elements like `ramp[2]`,
      // `obj.prop`, or `f(x)` fold their postfix operators into a single
      // element instead of leaking trailing tokens as extra siblings (issue #1).
      elements.push(buildExpressionWithPostfix(cursor, source));
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return { type: 'ArrayLiteral', elements, loc: nodeLoc };
}

function buildObjectLiteral(cursor: TreeCursor, source: string): ObjectLiteral {
  const nodeLoc = loc(cursor, source);
  const properties: (ObjectProperty | SpreadElement)[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === 'ObjectProperty') {
      cursor.firstChild();
      let key = '';
      let keyLoc: SourceLocation | undefined;
      let keyIsString = false;
      let hasValue = false;
      let value: Expression = { type: 'NullLiteral' };
      // First pass: find the key (first Identifier or String before ':')
      do {
        if ((cursor.name === 'Identifier' || cursor.name === 'String') && !key) {
          keyIsString = cursor.name === 'String';
          key = keyIsString ? parseStringContent(text(cursor, source)) : text(cursor, source);
          keyLoc = loc(cursor, source);
        } else if (cursor.name === ':') {
          // After ':', the rest is the value expression — use buildExpressionWithPostfix
          // to correctly handle Identifier + ArgList (function calls), member chains, etc.
          // A ':' with no value yet (mid-typing, error recovery) is still longhand.
          hasValue = true;
          if (cursor.nextSibling()) {
            value = buildExpressionWithPostfix(cursor, source);
          }
          break;
        }
      } while (cursor.nextSibling());
      cursor.parent();
      if (!hasValue && !keyIsString) {
        // Shorthand { key } — desugar to key: key so downstream consumers
        // (evaluators, scope analysis) see an ordinary identifier reference.
        // The key's loc anchors the reference for rename/navigation.
        // A colonless STRING key ({ 'a' } — grammar-invalid, reachable only
        // via error recovery) must NOT fabricate a reference from the string
        // content; it keeps the longhand NullLiteral shape below.
        properties.push({
          type: 'ObjectProperty',
          key,
          value: { type: 'Identifier', name: key, loc: keyLoc },
          shorthand: true,
        });
      } else {
        properties.push({ type: 'ObjectProperty', key, value });
      }
    } else if (cursor.name === 'SpreadElement') {
      cursor.firstChild();
      let arg: Expression = { type: 'NullLiteral' };
      do {
        // Postfix-aware builder (same reason as buildArrayLiteral — issue #1).
        if (cursor.name !== '...') arg = buildExpressionWithPostfix(cursor, source);
      } while (cursor.nextSibling());
      cursor.parent();
      properties.push({ type: 'SpreadElement', argument: arg });
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return { type: 'ObjectLiteral', properties, loc: nodeLoc };
}

function buildStyleBlockLiteral(cursor: TreeCursor, source: string): StyleBlockLiteral {
  // The interior tokenizes as interleaved StyleContent / StyleInterp runs
  // (a bare `${...}` in a value is its own token — see the grammar's
  // StyleInterp). Reconstruct the full interior span and hand it to
  // parseStyleDeclarations, whose value scanner is interp-aware.
  cursor.firstChild();
  let contentFrom = -1;
  let contentTo = -1;
  do {
    if (cursor.name === 'StyleBody') {
      contentFrom = cursor.from;
      contentTo = cursor.to;
    }
  } while (cursor.nextSibling());
  cursor.parent();

  const raw = contentFrom >= 0 && contentTo > contentFrom ? source.slice(contentFrom, contentTo) : '';
  const parsed = raw ? parseStyleDeclarations(raw, contentFrom, source) : { properties: [] };
  const node: StyleBlockLiteral = { type: 'StyleBlockLiteral', properties: parsed.properties };
  if (parsed.incomplete) node.incomplete = parsed.incomplete;
  return node;
}

const STYLE_NAME_START = /[a-zA-Z]/;
const STYLE_NAME_CHAR = /[a-zA-Z0-9-]/;

interface StyleIncomplete {
  message: string;
  line: number;
  column: number;
}

/** line/column (1-based) for an absolute source offset, matching loc(). */
function lineColAt(source: string, offset: number): { line: number; column: number } {
  return lineColumnAt(source, offset);
}

function lineColLoc(source: string, offset: number): SourceLocation {
  const { line, column } = lineColAt(source, offset);
  return { line, column, offset };
}

/**
 * Parse the raw `${ … }` style-block content into structured declarations.
 * Replaces the old regex scrape: this is quote-, paren-, and comment-aware,
 * and — unlike the regex, which silently dropped any declaration lacking a
 * trailing `;` — it treats a missing `:` or `;` as a hard error.
 *
 * AST-building stays LENIENT: on the first malformed declaration it stops and
 * returns an `incomplete` marker (with source line/column) alongside the
 * complete declarations parsed so far, rather than throwing. This keeps the
 * language service resilient while a block is being typed (scope analysis,
 * completion). The evaluator throws the marker as a strict error, so a missing
 * `;` still fails compilation and shows as an editor diagnostic (Phase 3 eval).
 * `baseOffset` is the absolute offset of `raw` within `source`.
 */
function parseStyleDeclarations(
  raw: string,
  baseOffset: number,
  source: string,
): { properties: StyleProperty[]; incomplete?: StyleIncomplete } {
  const properties: StyleProperty[] = [];
  let i = 0;
  const len = raw.length;
  const isWs = (ch: string) => ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f';
  const skipTrivia = () => {
    for (;;) {
      while (i < len && isWs(raw[i])) i++;
      if (i + 1 < len && raw[i] === '/' && raw[i + 1] === '/') {
        while (i < len && raw[i] !== '\n') i++; // skip to end of line
        continue;
      }
      break;
    }
  };
  const incompleteAt = (offset: number, message: string): { properties: StyleProperty[]; incomplete: StyleIncomplete } => {
    const { line, column } = lineColAt(source, offset);
    return { properties, incomplete: { message, line, column } };
  };

  for (;;) {
    skipTrivia();
    if (i >= len) break;

    const nameStart = i;
    if (!STYLE_NAME_START.test(raw[i])) {
      return incompleteAt(baseOffset + i, `Unexpected '${raw[i]}' in style block — expected a property name`);
    }
    while (i < len && STYLE_NAME_CHAR.test(raw[i])) i++;
    const name = raw.slice(nameStart, i);

    skipTrivia();
    if (raw[i] !== ':') {
      return incompleteAt(baseOffset + i, `Missing ':' after style property '${name}'`);
    }
    i++; // consume ':'
    skipTrivia();

    // Read the value up to a top-level ';' — quote- and paren-aware, stopping
    // at a `//` comment. An unterminated value that reaches end-of-content (or
    // a comment) is the missing-';' error.
    const valueStart = i;
    let depth = 0;
    let quote = '';
    let inTemplate = false;
    let interpDepth = 0;
    let bareInterp = 0;
    let terminated = false;
    while (i < len) {
      const ch = raw[i];
      if (quote) {
        if (ch === quote) quote = '';
        i++;
        continue;
      }
      if (bareInterp > 0) {
        // Inside a bare `${...}` interpolation: `;`, newlines, and `//` are
        // expression content — mirrors the template-interp machinery below.
        if (ch === '"' || ch === "'") {
          quote = ch;
          i++;
          continue;
        }
        if (ch === '{') bareInterp++;
        else if (ch === '}') bareInterp--;
        i++;
        continue;
      }
      if (inTemplate) {
        if (ch === '\\') {
          i += 2;
          continue;
        }
        if (interpDepth === 0) {
          // Template text: `;`, newlines, and `//` are all content here.
          if (ch === '`') inTemplate = false;
          else if (ch === '$' && raw[i + 1] === '{') {
            interpDepth = 1;
            i++; // extra advance for '{'
          }
          i++;
          continue;
        }
        // Inside a ${...} interpolation expression
        if (ch === '"' || ch === "'") {
          quote = ch;
          i++;
          continue;
        }
        if (ch === '{') interpDepth++;
        else if (ch === '}') interpDepth--;
        i++;
        continue;
      }
      if (ch === '"' || ch === "'") {
        quote = ch;
        i++;
        continue;
      }
      if (ch === '`') {
        inTemplate = true;
        i++;
        continue;
      }
      if (ch === '$' && raw[i + 1] === '{') {
        // Bare `${...}` interpolation opening at value top level (or inside
        // parens — the state machine above doesn't care about depth).
        bareInterp = 1;
        i += 2;
        continue;
      }
      if (ch === '(' || ch === '[') depth++;
      else if (ch === ')' || ch === ']') {
        if (depth > 0) depth--;
      } else if (depth === 0) {
        if (ch === ';') {
          terminated = true;
          break;
        }
        // A bare (unquoted, top-level) newline bounds the value, mirroring the
        // old regex's `[^;\n]+`. Without this, a declaration missing its `;`
        // would swallow the following declaration into a multi-line value
        // instead of being flagged — so a missing `;` on ANY declaration (not
        // just the last) is reported here.
        if (ch === '\n' || ch === '\r') break;
        if (ch === '/' && raw[i + 1] === '/') break; // comment before ';' → missing ';'
      }
      i++;
    }
    const rawValue = raw.slice(valueStart, i);
    const value = rawValue.trim();
    if (!terminated) {
      // Point just past the value's last non-whitespace char — where ';' was
      // expected — rather than at any trailing whitespace/newline consumed.
      return incompleteAt(
        baseOffset + valueStart + rawValue.trimEnd().length,
        inTemplate
          ? `Unterminated template literal in style value`
          : bareInterp > 0
            ? 'Unterminated ${...} interpolation in style value'
            : `Missing ';'`,
      );
    }
    i++; // consume ';'

    if (name) {
      const trimmedStart = valueStart + (rawValue.length - rawValue.trimStart().length);
      const trimmedEnd = valueStart + rawValue.trimEnd().length;
      properties.push({
        type: 'StyleProperty',
        name,
        value,
        loc: lineColLoc(source, baseOffset + nameStart),
        nameEnd: baseOffset + nameStart + name.length,
        valueLoc: lineColLoc(source, baseOffset + trimmedStart),
        valueEnd: baseOffset + trimmedEnd,
      });
    }
  }

  return { properties };
}

function buildPathBlockExpression(cursor: TreeCursor, source: string): PathBlockExpression {
  const nodeLoc = loc(cursor, source);
  // Path-block bodies are break/continue boundaries
  const body = atLoopBoundary(() => {
    const stmts: Statement[] = [];
    firstChildRaw(cursor);
    do {
      if (cursor.name !== 'pathBlockOpen' && cursor.name !== '}') {
        const stmt = buildStatement(cursor, source);
        if (stmt) stmts.push(stmt);
      }
    } while (nextSiblingRaw(cursor));
    cursor.parent();
    return stmts;
  });
  return { type: 'PathBlockExpression', body, loc: nodeLoc };
}

function buildTextBlockExpression(cursor: TreeCursor, source: string): TextBlockExpression {
  const nodeLoc = loc(cursor, source);
  // Text-block-expression bodies are break/continue boundaries
  const body = atLoopBoundary(() => {
    const stmts: Statement[] = [];
    firstChildRaw(cursor);
    do {
      if (cursor.name !== 'textBlockOpen' && cursor.name !== '}') {
        const stmt = buildStatement(cursor, source);
        if (stmt) stmts.push(stmt);
      }
    } while (nextSiblingRaw(cursor));
    cursor.parent();
    return stmts;
  });
  return { type: 'TextBlockExpression', body, loc: nodeLoc };
}

function buildLayerConstructor(cursor: TreeCursor, source: string): LayerConstructorExpression {
  const nodeLoc = loc(cursor, source);
  let layerType: 'PathLayer' | 'TextLayer' | 'GroupLayer' = 'PathLayer';
  let name: Expression = { type: 'StringLiteral', value: '' };
  let styleExpr: Expression | undefined;

  cursor.firstChild();
  let inParens = false;
  let foundName = false;
  do {
    if (cursor.name === 'LayerType') {
      layerType = text(cursor, source) as 'PathLayer' | 'TextLayer' | 'GroupLayer';
    } else if (cursor.name === '(') {
      inParens = true;
    } else if (cursor.name === ')') {
      inParens = false;
    } else if (inParens && !foundName && isExpressionNode(cursor.name)) {
      // Postfix-aware: PathLayer(names[i]) constructor form. The walker
      // may rest ON the ')' — resync inParens so the style block isn't
      // mistaken for in-paren content.
      name = buildExpressionWithPostfix(cursor, source);
      foundName = true;
      if ((cursor.name as string) === ')') inParens = false;
    } else if (!inParens && cursor.name === 'StyleBlockLiteral') {
      styleExpr = buildStyleBlockLiteral(cursor, source);
    }
  } while (cursor.nextSibling());
  cursor.parent();

  return { type: 'LayerConstructorExpression', layerType, name, styleExpr, loc: nodeLoc };
}

function buildLayerCallExpression(cursor: TreeCursor, source: string): FunctionCall {
  const nodeLoc = loc(cursor, source);
  const args: Expression[] = [];
  cursor.firstChild();
  let inParens = false;
  do {
    if (cursor.name === '(') inParens = true;
    else if (cursor.name === ')') inParens = false;
    else if (inParens && isExpressionNode(cursor.name)) {
      // Postfix-aware: layer(cfg.name) as a VALUE expression (review
      // finding — same flatten class as the six item J sites; plain
      // buildExpression split cfg.name into two args). Resync inParens
      // when the walker rests ON ')'.
      args.push(buildExpressionWithPostfix(cursor, source));
      if ((cursor.name as string) === ')') inParens = false;
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return { type: 'FunctionCall', name: 'layer', args, loc: nodeLoc };
}

function buildParenExpression(cursor: TreeCursor, source: string): Expression {
  cursor.firstChild();
  let expr: Expression = { type: 'NullLiteral' };
  do {
    if (cursor.name !== '(' && cursor.name !== ')') {
      // Use buildExpressionWithPostfix so that postfix tokens (`.`, ArgList, `[`)
      // flattened as siblings inside the ParenExpression are properly consumed
      // as part of the expression chain rather than overwriting it.
      expr = buildExpressionWithPostfix(cursor, source);
      break; // postfix siblings already consumed
    }
  } while (cursor.nextSibling());
  cursor.parent();
  return expr;
}

// --- Block builder ---

function buildBlock(cursor: TreeCursor, source: string): Statement[] {
  const stmts: Statement[] = [];
  firstChildRaw(cursor); // a Block always has its braces
  do {
    if (cursor.name === '{' || cursor.name === '}') continue;
    // Comments in block bodies stay (buildStatement builds them) so the
    // formatter can round-trip them
    const stmt = buildStatement(cursor, source);
    if (stmt) stmts.push(stmt);
  } while (nextSiblingRaw(cursor));
  cursor.parent();
  return stmts;
}

// --- Utility ---

function extractVariableNames(cursor: TreeCursor, source: string): string[] {
  const names: string[] = [];
  cursor.firstChild();
  do {
    if (cursor.name === 'VariableName') names.push(text(cursor, source));
  } while (cursor.nextSibling());
  cursor.parent();
  return names;
}

function isExpressionNode(name: string): boolean {
  return name === 'TernaryExpression' || name === 'BinaryExpression' ||
    name === 'UnaryExpression' || name === 'Number' || name === 'String' ||
    name === 'TemplateLiteral' || name === 'BooleanLiteral' || name === 'NullLiteral' ||
    name === 'ColorLiteral' || name === 'CSSColorLiteral' || name === 'ArrayLiteral' ||
    name === 'ObjectLiteral' || name === 'StyleBlockLiteral' || name === 'PathBlockExpression' ||
    name === 'TextBlockExpression' || name === 'CalcExpression' || name === 'LayerConstructor' || name === 'LayerCallExpression' ||
    name === 'Identifier' || name === 'ParenExpression' || name === 'RangeExpression' || name === 'ArgList' ||
    name === 'TrailingBlock' || name === 'SwitchExpression';
}

function isOperator(name: string): boolean {
  return name === '+' || name === '-' || name === '*' || name === '/' || name === '%' ||
    name === '==' || name === '!=' || name === '<=' || name === '>=' || name === '<' ||
    name === '>' || name === '||' || name === '&&' || name === '<<';
}

function parseStringContent(raw: string): string {
  // Remove surrounding quotes and unescape
  const inner = raw.slice(1, -1);
  return inner
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
    .replace(/\\\\/g, '\\')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'");
}

function offsetToLoc(offset: number, source: string): SourceLocation {
  // Called once per path argument — the hottest caller of lineColumnAt.
  const { line, column } = lineColumnAt(source, offset);
  return { line, column, offset };
}
